// Neu (SPEC 03 §2, §9) — die deutschen Oberflächensätze an EINER Stelle.
//
// Wörtlich aus SPEC 03 übernommen, wo die Spezifikation sie zitiert (§7.1 DoD Punkt 7). Die
// Bildschirme lesen hier und schreiben keinen Satz zweimal: ein zweiter Wortlaut wäre der, der
// beim nächsten Mal vergessen wird. Kein Satz enthält Daten eines Kindes; Platzhalter werden
// über Funktionen gefüllt.
import type { ChipTone } from "../components/ui/primitives";
import type { AuftragSchritt, BogenOffenGrund, Bezug, Flag, Freigabe, GehaltenGrund, KaestchenMerkmal, KaestchenVergleich, KaestchenZustand, OrtArt, OsmUmfang, PausenGrund, Recht, Rolle, Schritt, Sicherheit, Stufe, VerortungGrund, Zustand } from "../api/typen";
import { nf, prozent } from "./format";

/* ------------------------------------------------------------------ Kennzeichen (01 §2.8.1) */

/** Deutsche Namen der Prüf-Kennzeichen (SPEC 03 §9.7.2) — überall, wo ein Flag als Chip steht. */
export const FLAG_LABEL: Record<Flag, string> = {
  a_ungleich_n: "Lesungen weichen ab",
  unleserlich_markiert: "* oder [?] im Text",
  unsicher: "KI unsicher",
  schema: "Antwort nicht im Schema",
  leer_uneinig: "leer/gefüllt uneinig",
  lexikon: "Wort nicht im Wörterbuch",
  name: "Name erkannt",
  ankreuz_unklar: "Kästchen unklar",
  frage_fehlt: "Frage unsicher",
  stichprobe: "Stichprobe",
  gruppenbogen: "Gruppenbogen?",
  dublette: "Doppelt gescannt?",
  personenbezug_verdacht: "möglicher Name im Text",
  wert_unplausibel: "Wert unplausibel",
  in_maske: "Text unter Schwärzung",
  bbox_klein: "Rahmen zu klein",
  zeichnung: "Zeichnung bestätigen",
  lesen_fehlgeschlagen: "Seite nicht lesbar",
  lage_unsicher: "Lage unsicher",
  leerseite: "Leerseite?",
  kaestchen_abweichung: "Kästchen-Abweichung",
  kaestchen_ohne_region: "Angekreuzt ohne Region",
  // E33 (Rahmen): Korrektur = ganz ausgemaltes Kästchen neben einem Kreuz; die beiden anderen sind Hinweise
  kaestchen_korrektur: "Korrektur im Kästchen (ausgemalt)",
  kaestchen_ohne_vorlage: "Seite ohne gelernte Vorlage",
  kaestchen_registrierung: "Seite nicht ausgerichtet",
  // E35: ein Feld der Bogenvorlage mit Kästchen, aber ohne Antwortstelle auf der Seite — nur ein Hinweis
  kaestchen_vorlage_ohne_region: "Ankreuzfrage der Bogenvorlage ohne Region",
};

/** Gründe, die nur an Seitenposten hängen (art 'seite', keine Region): die Warteschlange listet nur
 *  Regionen, ein Filter darauf bliebe immer leer. Sie stehen in der Seiten-/Bogenansicht. */
export const SEITEN_FLAGS: ReadonlySet<Flag> = new Set<Flag>([
  "kaestchen_ohne_region", "leerseite", "lesen_fehlgeschlagen",
  "kaestchen_ohne_vorlage", "kaestchen_registrierung", // E33: Hinweise an der Seite, nie an einer Region
  "kaestchen_vorlage_ohne_region",                    // E35
]);

const FLAG_FEHLER = new Set<Flag>(["name", "in_maske", "personenbezug_verdacht", "lesen_fehlgeschlagen"]);

/** Ton des Chips: Namen/Schwärzung/unlesbar rot, Stichprobe teal, alles andere warn (§9.7.2). */
export function flagTon(f: Flag): ChipTone {
  if (FLAG_FEHLER.has(f)) return "error";
  if (f === "stichprobe") return "teal";
  return "warn";
}

export function flagLabel(f: string): string {
  return (FLAG_LABEL as Record<string, string>)[f] ?? f;
}

/* ------------------------------------------------------------------ Zeichnung (01 zeichnung.dinge) */

/** Ein Eintrag je Schlüssel des CHECK auf `zeichnung.dinge` (01 §1), Umlaute zurückgesetzt. */
export const DING_LABEL: Record<string, string> = {
  schaukel: "Schaukel",
  nestschaukel: "Nestschaukel",
  rutsche: "Rutsche",
  klettergeruest: "Klettergerüst",
  kletterwand: "Kletterwand",
  seilbahn: "Seilbahn",
  trampolin: "Trampolin",
  wippe: "Wippe",
  karussell: "Karussell",
  sandkasten: "Sandkasten",
  wasser: "Wasser",
  matsch: "Matsch",
  baumhaus: "Baumhaus",
  huette: "Hütte",
  tunnel: "Tunnel",
  baum: "Baum",
  blume: "Blume",
  wiese: "Wiese",
  gebuesch: "Gebüsch",
  bank: "Bank",
  tisch: "Tisch",
  sonnensegel: "Sonnensegel",
  schatten: "Schatten",
  fussball: "Fußball",
  basketball: "Basketball",
  tischtennis: "Tischtennis",
  skaten: "Skaten",
  fahrrad: "Fahrrad",
  toilette: "Toilette",
  trinkbrunnen: "Trinkbrunnen",
  muelleimer: "Mülleimer",
  licht: "Licht",
  zaun: "Zaun",
  weg: "Weg",
  tier: "Tier",
  sonne: "Sonne",
  mensch: "Mensch",
  sonstiges: "Sonstiges",
};

/* ------------------------------------------------------------------ Zustände, Stufen, Rollen */

export const ZUSTAND_LABEL: Record<Zustand, string> = {
  text: "Text",
  leer: "leer",
  keine_angabe: "keine Angabe",
  unleserlich: "unleserlich",
  durchgestrichen: "durchgestrichen",
  nicht_zutreffend: "nicht zutreffend",
};

export const STUFE_LABEL: Record<Stufe, string> = {
  raster: "Raster",
  maske: "Maskieren",
  lesen: "Lesen",
  pruefen: "Prüfen",
  fertig: "fertig",
};
export const STUFEN: Stufe[] = ["raster", "maske", "lesen", "pruefen", "fertig"];

export const SCHRITT_LABEL: Record<Schritt, string> = {
  lesen: "Seiten lesen",
  nachlesen: "Ausschnitte nachlesen",
  // E37: nur mit SEITENANALYSE_AKTIV — das Sehmodell sieht sich gefundene Zeichnungen an (nichts zählt von selbst)
  zeichnung_deuten: "Zeichnungen deuten",
  // E31: das lokale Textmodell fasst die Schreibweisen der gedruckten Fragen zusammen (Vorschlag)
  fragen_ordnen: "KI ordnet die Fragen",
  lesefassung: "Lesefassung schreiben",
  verorten: "Orte zuordnen",
  kodieren: "Themen zuordnen",
  katalog_entwurf: "Themen-Entwurf",
};

/** Alle Auftragsschritte (Aufträge-Knopf der Kopfleiste): die drei CPU-Schritte nach dem Import
 *  und die GPU-Schritte des Laufs. `einheit`/`taetigkeit` füllen die Zeile von <Fortschritt>
 *  („Dokument 3 von 12 gerastert“) — die Einheit ist das Ziel des Auftrags im Server. */
export const AUFTRAG_LABEL: Record<AuftragSchritt, { text: string; einheit: string; taetigkeit: string }> = {
  raster: { text: "Seiten rastern", einheit: "Dokument", taetigkeit: "gerastert" },
  // Seit E19 sucht der Schritt `maske` auch gedruckte Namensfelder (lokal, Tesseract); nicht jede
  // Seite wird dabei geschwärzt (ohne Befund bleibt sie gehalten) — darum „bearbeitet“.
  maske: { text: "Namensfelder erkennen und schwärzen", einheit: "Seite", taetigkeit: "bearbeitet" },
  boegen: { text: "Bögen bilden", einheit: "Dokument", taetigkeit: "aufgeteilt" },
  // E36: CPU, ohne KI — übernimmt nur eindeutige Gruppierungsvorschläge, je Verfahren.
  boegen_sicher: { text: "Eindeutige Bögen übernehmen", einheit: "Verfahren", taetigkeit: "geprüft" },
  // E24: prüft je Verfahren, welche Seiten wirklich dasselbe Blatt zweimal sind (lokal, ohne KI).
  dublette: { text: "Dubletten prüfen", einheit: "Verfahren", taetigkeit: "geprüft" },
  // E33: lernt je Verfahren aus den Scans, wo die Kästchen jeder Formularseite stehen (lokal, ohne KI).
  rahmen: { text: "Vorlagen lernen", einheit: "Verfahren", taetigkeit: "gelernt" },
  // E23: zweiter Auftrag nach dem Lesen — lokal auf der CPU, ohne KI (02 §2.4a).
  kaestchen: { text: "Kästchen messen", einheit: "Seite", taetigkeit: "gemessen" },
  // E37: lokal auf der CPU, ohne KI — was auf jeder Seite steht (Marken, Schrift, Zeichnungen, Karten).
  seitenanalyse: { text: "Seiten analysieren", einheit: "Seite", taetigkeit: "analysiert" },
  zeichnung_deuten: { text: SCHRITT_LABEL.zeichnung_deuten, einheit: "Seite", taetigkeit: "gedeutet" },
  lesen: { text: SCHRITT_LABEL.lesen, einheit: "Seite", taetigkeit: "gelesen" },
  nachlesen: { text: SCHRITT_LABEL.nachlesen, einheit: "Ausschnitt", taetigkeit: "nachgelesen" },
  // E31: ein Auftrag je Verfahren — die Fragenliste als Vorschlag (übernommen wird nichts von selbst).
  fragen_ordnen: { text: SCHRITT_LABEL.fragen_ordnen, einheit: "Auftrag", taetigkeit: "erledigt" },
  // E34: CPU, ohne KI — übernimmt nur die sicheren Fragen des Abgleichs, je Verfahren.
  fragen_sicher: { text: "Sichere Fragen übernehmen", einheit: "Verfahren", taetigkeit: "geprüft" },
  lesefassung: { text: SCHRITT_LABEL.lesefassung, einheit: "Antwort", taetigkeit: "geschrieben" },
  // E21: das Textmodell wählt unter den Kandidaten des Ortsverzeichnisses — je Antwort ein Auftrag.
  verorten: { text: SCHRITT_LABEL.verorten, einheit: "Antwort", taetigkeit: "zugeordnet" },
  // E21: CPU-Auftrag (OSM, Datei, Kopie, Neuzuordnung) — ein Auftrag je Ladevorgang.
  ortsimport: { text: "Ortsverzeichnis laden", einheit: "Auftrag", taetigkeit: "erledigt" },
  // E21h: das handschriftliche Ortsteil-Feld der Umfrage — lokal auf der CPU, ohne KI, je Seite.
  ortsteilfeld: { text: "Ortsteil-Feld zuordnen", einheit: "Seite", taetigkeit: "zugeordnet" },
  katalog_entwurf: { text: SCHRITT_LABEL.katalog_entwurf, einheit: "Auftrag", taetigkeit: "erledigt" },
  kodieren: { text: SCHRITT_LABEL.kodieren, einheit: "Auftrag", taetigkeit: "erledigt" },
  // E35: Bogenvorlage aus dem leeren Bogen — CPU je Verfahren, GPU je Blatt, CPU je Seite.
  leerbogen: { text: "Leeren Bogen bilden", einheit: "Verfahren", taetigkeit: "gebildet" },
  formular_lesen: { text: "Bogenvorlage lesen", einheit: "Blatt", taetigkeit: "gelesen" },
  vorlage: { text: "Bogenvorlage anwenden", einheit: "Seite", taetigkeit: "angewandt" },
};
/** Unbekannter Schritt vom Server (neuer Schritt, alte Oberfläche): roher Name, neutrale Einheit. */
export function auftragLabel(s: string): { text: string; einheit: string; taetigkeit: string } {
  return (AUFTRAG_LABEL as Record<string, { text: string; einheit: string; taetigkeit: string }>)[s] ?? { text: s, einheit: "Auftrag", taetigkeit: "erledigt" };
}

/** Grund, warum eine Seite gehalten ist (GET …/masken/offen, E19) — Chip auf der Karte unter
 *  „Gehaltene Seiten“. `aktion`: muss ein Mensch etwas tun (schwärzen oder freigeben)? */
export const GEHALTEN_GRUND: Record<GehaltenGrund, { text: string; tone: ChipTone; icon: string; aktion: boolean }> = {
  erkennung_offen: { text: "Namensfeld-Erkennung läuft", tone: "neutral", icon: "hourglass", aktion: false },
  erkennung_ausstehend: { text: "Erkennung noch nicht angestoßen — „Namensfelder erkennen“ drücken", tone: "warn", icon: "scan", aktion: false },
  erkennung_fehler: { text: "Erkennung fehlgeschlagen — bitte schwärzen", tone: "error", icon: "warning-circle", aktion: true },
  kein_namensfeld: { text: "kein Namensfeld erkannt — bitte schwärzen", tone: "warn", icon: "warning", aktion: true },
  keine_vorlage: { text: "keine Vorlage", tone: "warn", icon: "warning", aktion: true },
  keine_charge_maske: { text: "keine Charge-Schwärzung", tone: "warn", icon: "warning", aktion: true },
};
/** Unbekannter Grund vom Server (neuer Grund, alte Oberfläche): roher Name, Warnton. */
export function gehaltenGrund(g: string): { text: string; tone: ChipTone; icon: string; aktion: boolean } {
  return (GEHALTEN_GRUND as Record<string, { text: string; tone: ChipTone; icon: string; aktion: boolean }>)[g] ?? { text: g, tone: "warn", icon: "warning", aktion: true };
}

export const ROLLE_LABEL: Record<Rolle, string> = { admin: "Admin", pruefer: "Prüfer", analyst: "Analyst" };

export const FREIGABE_LABEL: Record<Freigabe, string> = { keine: "keine", text: "Text", crops: "Ausschnitte", zeichnungen: "Zeichnungen" };

export const RECHT_LABEL: Record<Recht, string> = {
  lesen: "lesen",
  assistent: "Assistent",
  exportieren: "exportieren",
  rohdaten: "Rohdaten",
  hochladen: "hochladen",
  maskieren: "maskieren",
  maske_freigeben: "Maske freigeben",
  lauf: "Lauf",
  pruefen: "prüfen",
  original: "Original",
  original_pdf: "Original-PDF",
  kodieren: "kodieren",
  katalog: "Katalog",
  verwalten: "verwalten",
  orte: "Ortsverzeichnis",
};

/** Regionsarten für „+ Region“ (SPEC 03 §9.7.3). */
export const REGION_ARTEN: { value: string; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "ankreuz", label: "Ankreuzen" },
  { value: "skala", label: "Skala" },
  { value: "zahl", label: "Zahl" },
  { value: "zeichnung", label: "Zeichnung" },
  { value: "sonstiges", label: "Sonstiges" },
];

/* ------------------------------------------------------------------ Verortung (E21) */

/** Die Namensnennung der OSM-Daten (E21f) — wortgleich mit `config.OSM_NAMENSNENNUNG` im Backend. */
export const OSM_NAMENSNENNUNG =
  "Kartendaten © OpenStreetMap-Mitwirkende, verfügbar unter der Open Database License (ODbL) – https://www.openstreetmap.org/copyright";
export const OSM_COPYRIGHT_URL = "https://www.openstreetmap.org/copyright";

/** Deutsche Namen der Ortsarten — wortgleich mit `ortsnamen.ART_LABEL` im Backend. */
export const ORT_ART_LABEL: Record<OrtArt, string> = {
  spielplatz: "Spielplatz",
  park: "Park",
  gruen: "Grünfläche",
  wald: "Wald",
  sportplatz: "Sportplatz",
  sporthalle: "Sporthalle",
  schwimmbad: "Schwimmbad",
  schule: "Schule",
  kita: "Kita",
  jugend: "Jugendtreff",
  kirche: "Kirche",
  bibliothek: "Bibliothek",
  laden: "Laden",
  gewaesser: "Gewässer",
  fluss: "Fluss/Bach",
  strasse: "Straße",
  platz: "Platz",
  bahnhof: "Bahnhof",
  ortsteil: "Ortsteil",
  sonstiges: "Ort",
};
export const ORT_ARTEN = Object.keys(ORT_ART_LABEL) as OrtArt[];
export function ortArtLabel(a: string | null | undefined): string {
  return (a && (ORT_ART_LABEL as Record<string, string>)[a]) || "Ort";
}

export const BEZUG_LABEL: Record<Bezug, string> = { ort: "Ort", wohnort: "Wohnort", bezirk: "Stadtteil" };
export const SICHERHEIT_LABEL: Record<Sicherheit, string> = { hoch: "sicher", mittel: "eher sicher", niedrig: "unsicher" };
export function sicherheitTon(s: Sicherheit | null): ChipTone {
  return s === "hoch" ? "good" : s === "mittel" ? "warn" : "neutral";
}

/** Warum das Verfahren diesen Ort vorschlägt (feste Liste aus 084, `verortung.grund`). */
export const VERORTUNG_GRUND: Record<VerortungGrund, string> = {
  name: "Name steht so im Text",
  alias: "bekannter anderer Name",
  schreibweise: "ähnliche Schreibweise",
  kern: "Kern des Namens passt",
  gattung: "allgemeines Wort nahe der Schule",
  schulnaehe: "nahe der Schule der Charge",
  kontext: "aus dem Zusammenhang gewählt",
  bezirk_genannt: "Stadtteil genannt",
  wohnort_hinweis: "Hinweis auf Wohnort",
  ohne_ki: "ohne KI-Wahl (Kandidatensuche)",
  mensch: "von Hand gesetzt",
  stadtname: "Name der Stadt (zählt zur Kernstadt)",
};

/** E21h: Ortsbezug der Auswertung (Filter `ortsbezug`, wirkt auf die Dimension Stadtteil). */
export const ORTSBEZUG_TEXT = {
  segment: { alle: "alle", ort: "genannte Orte", wohnort: "Wohnort (Ortsteil-Feld)" },
  chip: { alle: "alle", ort: "genannte Orte (ohne Wohnorte)", wohnort: "Wohnort je Kind (Ortsteil-Feld)" },
  titel: {
    alle: "alle Ortsangaben",
    ort: "Orte, die Kinder nennen (Ziele, Treffpunkte) — ohne Wohnorte",
    wohnort: "wo Kinder wohnen: je Kind EIN Stadtteil, aus dem Ortsteil-Feld der Umfrage (sonst aus Wohnort-Angaben in Antworten); widersprüchlich = „unklar“",
  },
} as const;
export function verortungGrund(g: string): string {
  return (VERORTUNG_GRUND as Record<string, string>)[g] ?? g;
}

export const OSM_UMFANG: { id: OsmUmfang; label: string }[] = [
  { id: "bezirke", label: "Stadtteile (Flächen, falls vorhanden)" },
  { id: "orte_ortsteile", label: "Ortsteile, Weiler, Quartiere (Punkte)" },
  { id: "spiel_sport", label: "Spielplätze, Sport, Bäder" },
  { id: "schulen_kitas", label: "Schulen und Kitas" },
  { id: "gruen_wasser", label: "Parks, Wald, Gewässer" },
  { id: "laeden", label: "Läden mit Namen" },
  { id: "kirchen_kultur", label: "Kirchen, Bibliothek, Jugendtreffs, Bahnhof" },
  { id: "strassen", label: "Straßennamen (nur für Stadtteil-Zuordnung, nie als Ort)" },
];

/** Zustand eines gemessenen Kästchens (E23) — auch für Vorlesetexte. */
const TX_KAESTCHEN_ZUSTAND: Record<KaestchenZustand, string> = { leer: "leer", angekreuzt: "angekreuzt", unklar: "unklar" };
/** E33: warum die Vorlagenmessung einen Zustand so gesetzt hat (nur Vorlagenkästchen; blind: null). */
const TX_KAESTCHEN_MERKMAL: Record<KaestchenMerkmal, string> = {
  korrektur: "Korrektur",
  ausgemalt_allein: "ausgemalt",
  blass: "blass",
  strich: "Strich",
  randnah: "am Rand",
  schwach: "schwach",
  ohne_beleg: "ohne Beleg",
  blass_unsicher: "blass, unsicher",
};

/* ------------------------------------------------------------------ Sätze je Bildschirm */

export const TX = {
  // E36 (02 §3.5a): die drei Prüfmodi der Auswertung (Facette „Prüfstatus“, Filterchips, Beschreibungen)
  pruefmodus: {
    label: { angenommen: "angenommene Antworten", geprueft: "nur ganz geprüfte Bögen", alle: "inkl. ungeprüfter" } as Record<"angenommen" | "geprueft" | "alle", string>,
    titel: {
      angenommen: "Jede angenommene Antwort zählt sofort — auch wenn andere Antworten desselben Bogens noch offen sind.",
      geprueft: "Nur Bögen, deren Antworten alle angenommen sind — per Regel oder von Menschen geprüft, auch in automatisch gebildeten Bögen.",
      alle: "Auch Antworten, die noch niemand geprüft hat.",
    } as Record<"angenommen" | "geprueft" | "alle", string>,
  },
  // E35 (03 §9.7.8): Prüfen → „Bogenvorlage“ — gedruckter Text des leeren Bogens, nie Kinderschrift.
  bogenvorlage: {
    titel: "Bogenvorlage",
    unterzeile:
      "Aus vielen ausgerichteten Scans derselben Seite entsteht ein leerer Bogen: nur der Druck, die Handschrift mittelt sich heraus, Schwärzungen bleiben schwarz. Das Sehmodell liest ihn zweimal; die Lage der Kästchen setzt der Server. Erst was hier bestätigt und festgeschrieben ist, gilt.",
    aus: "Die Bogenvorlage aus dem leeren Bogen ist nicht eingeschaltet (FORMULAR_AKTIV). Die Fragenliste arbeitet wie bisher.",
    keinEntwurf: "Noch kein Entwurf",
    keinEntwurfSub: "„Bogenvorlage lesen“ bildet die leeren Bögen und lässt sie lesen — ein Auftrag je Blatt, sichtbar in den Aufträgen.",
    lesen: "Bogenvorlage lesen",
    neuLesen: "Neu lesen",
    neuLesenTitel: "Bestätigte Fragen bleiben; die neue Lesung liegt daneben.",
    lesenEingereiht: (lb: number, n: number) => `Eingereiht: ${lb ? "leere Bögen bilden · " : ""}${n} ${n === 1 ? "Blatt" : "Blätter"} lesen`,
    neueLesung: "Neue Lesung übernehmen",
    titelLabel: "Titel der Bogenvorlage",
    pille: (b: number, n: number, offen: number, sicher: number) => `Entwurf · ${nf(b)} von ${nf(n)} bestätigt · ${nf(offen)} offen · ${nf(sicher)} sicher`,
    pilleFest: (key: string, n: number) => `Festgeschrieben · ${key} · ${nf(n)} Fragen`,
    blattTab: (nr: number, seiten: number, name: boolean) => `Blatt ${nr} · ${nf(seiten)} Seiten${name ? " · Namensfeld" : ""}`,
    gehoertNicht: "gehört nicht zum Bogen",
    blattSeite: "Seite im Bogen",
    blattSeiteVorschlag: (n: number) => `Vorschlag: Seite ${n}`,
    blattStatus: {
      zu_wenig: "zu wenige unabhängige Bögen",
      abgelehnt: "leerer Bogen nicht freigegeben",
      geprueft: "leerer Bogen bereit",
      gelesen: "gelesen",
      ersetzt: "ersetzt",
    } as Record<string, string>,
    lesenStand: { wartet: "Lesen wartet", laeuft: "wird gelesen", fertig: "gelesen", fehlgeschlagen: "Lesen fehlgeschlagen" } as Record<string, string>,
    zuWenig: (n: number, min: number) => `Leerer Bogen: zu wenige unabhängige Bögen (${nf(n)} von ${nf(min)})`,
    hinweis: {
      blass: "Der Druck ist blass — Kästchen könnten fehlen.",
      zu_viele_kaestchen: "Mehr Kästchen als erwartet — bitte die Zuordnung prüfen.",
      veraltet: "Der leere Bogen ist veraltet (eine Quellseite änderte sich) — er wird neu gebildet.",
      neue_lesung: "Eine neue Lesung liegt vor.",
    } as Record<string, string>,
    bildFehlt: "Der leere Bogen ist (noch) nicht freigegeben.",
    // Stufen der Fragen und Kästchen (Überlagerung, Liste, Vorlesenamen)
    status: { offen: "offen", bestaetigt: "bestätigt", verworfen: "verworfen", problem: "bitte prüfen" } as Record<string, string>,
    boxStatus: {
      sicher: "sicher",
      wahrscheinlich: "wahrscheinlich",
      mensch: "von Hand",
      ohne_kaestchen: "ohne Kästchen",
      marke_widerspricht: "Marken widersprechen sich",
      mehrdeutig: "mehrdeutig",
      formular: "offen",
      erfunden: "nicht gedruckt",
    } as Record<string, string>,
    // E35b: eine Option ohne Kästchen, deren Feld auf dem Bogen liegt (Kreis, Wort, Gesicht) — nie gemessen
    antwortfeld: "Antwortfeld ohne Kästchen",
    antwortfeldArt: { kreis: "Kreis", zone: "Feld" } as Record<string, string>,
    antwortfeldTitel: (art: string) => `Antwortfeld ohne Kästchen (${art}) — hier wird nichts gemessen`,
    antwortfeldSatz: "Kreuze und Kreise darin misst der Server nicht — die Antwort liest das Modell auf jeder Seite.",
    // E35c: die Gruppen der Vorlage ordnen den Entwurf — was keine Lesung kannte, bleibt leer, nie erfunden
    frageOhneText: "Fragetext fehlt – bitte vom Bogen abschreiben",
    frageOhneTextKurz: "(Fragetext fehlt)",
    frageOhneTextSatz: "Keine Lesung fand den gedruckten Text dieser Frage. Ihre Kästchen gehören laut Vorlage zu einer Frage – schreib den Text vom Bogen ab.",
    optionOhneText: "Text fehlt – vom Bogen abschreiben",
    artGeerbtSatz: "Diese Frage war mit einer anderen zu einer zusammengelesen und wurde getrennt; ihre Antwortart galt beiden – bitte prüfen: eine oder mehrere Antworten?",
    optionOhneTextSatz: "Diese Kästchen gehören laut Vorlage zur Frage, aber keine Lesung nannte ihren Text – bitte vom Bogen abschreiben.",
    wort: {
      ungedeckt: "nur in einer Lesung oder in keinem gelesenen Bogen",
      etikett: "so lasen es die Bögen",
      widerspruch: "Lesungen widersprechen sich (nicht/kein)",
    },
    frage: (nr: string | number) => `Frage ${nr}`,
    text: "Text",
    beleg: (seiten: number, varianten: number) => `In ${nf(seiten)} Bögen beantwortet${varianten > 1 ? ` · ${nf(varianten)} Schreibweisen` : ""}`,
    lesungen: (l1: string | null, l2: string | null) => (l1 && l2 && l1 !== l2 ? `Lesung 1: „${l1}“ · Lesung 2: „${l2}“` : ""),
    art: "Antwortart",
    arten: {
      freitext: "Freitext",
      einfach: "Ankreuzen (eine)",
      mehrfach: "Ankreuzen (mehrere)",
      skala: "Skala",
      smiley: "Smileys",
      zahl: "Zahl",
      zeichnung: "Zeichnung",
      name: "Name",
      alter: "Alter",
      klasse: "Klasse",
    } as Record<string, string>,
    richtung: "Skala-Richtung",
    richtungen: { "": "— bitte wählen —", hoch_ist_gut: "hoch ist gut", niedrig_ist_gut: "niedrig ist gut" } as Record<string, string>,
    optionen: "Optionen",
    kaestchenWaehlen: "Kästchen wählen",
    kaestchenWaehlenAktiv: "Jetzt ein Kästchen auf dem Bogen antippen …",
    kaestchenLoesen: "Kästchen lösen",
    zoomen: "Zum Zuweisen heranzoomen",
    optionLabel: (code: string) => `Bezeichnung der Option ${code}`,
    personenbezogen: "personenbezogen",
    personGrund: { art: "Name, Alter und Klasse sind immer personenbezogen.", wortliste: "Die Frage fragt nach der Person.", schwaerzung: "Die Frage liegt in einer Schwärzung.", mensch: "Von Hand gesetzt." } as Record<string, string>,
    entspricht: "Entspricht in der Fragenliste",
    neueFrage: "neue Frage",
    fragenlisteOption: (key: string, text: string) => `${key} ‚${text.length > 48 ? `${text.slice(0, 47)}…` : text}‘`,
    gehtAuf: "geht auch auf",
    gehtAufDazu: "geht auch auf: hinzufügen",
    gehtAufLeer: "keine",
    aufgehendWeg: (key: string) => `${key} nicht mehr aufnehmen`,
    aufgehendUnbekannt: "eine Frage, die es nicht mehr gibt",
    vorschlagAnteil: (p: number) => `Vorschlag (${prozent(p)})`,
    vorschlagAufgehend: (keys: string) => `Vorschlag: geht auch auf ${keys}`,
    gehtAufKurz: (keys: string) => `geht auch auf: ${keys}`,
    karteZeigen: "Ansehen",
    karteZeigenTitel: "Die Frage legt Fragen der Fragenliste zusammen — erst die ganze Karte ansehen, dann bestätigen.",
    personVonFragenliste: "Eine zugeordnete Frage der Fragenliste ist personenbezogen — bitte als personenbezogen markieren.",
    bestaetigen: "Bestätigen",
    verwerfen: "Verwerfen (keine Frage)",
    wiederOeffnen: "Wieder öffnen",
    teilen: "Teilen",
    teilenNach: (k: number) => `Nach Option ${k} teilen`,
    zusammenlegen: "Mit nächster zusammenlegen",
    listenModus: "Liste",
    buehnenModus: "Bogen",
    nurOffene: "nur offene",
    sichereBestaetigen: (n: number) => `${nf(n)} sichere bestätigen`,
    sichereTitel: "Nur Fragen, die beide Lesungen und die Lage stützen — personenbezogene nie auf einmal.",
    freiGruppen: (n: number) => `Kästchengruppen ohne Frage (${nf(n)})`,
    fehlende: (n: number) => `Beantwortet, fehlt im Entwurf (${nf(n)})`,
    alsFrage: "Als Frage hinzufügen",
    gruppe: (n: number) => `${nf(n)} Kästchen`,
    fehlend: (label: string, seiten: number) => `„${label}“ · ${nf(seiten)} Seiten`,
    vorschau: "Vorschau",
    vorschauSatz: (a: number, b: number, c: number, d: number, e: number) =>
      `Festschreiben würde: ${nf(a)} von ${nf(b)} Ankreuzfragen ihre Kästchen geben · ${nf(c)} Antworten einer Frage zuordnen · ${nf(d)} Widersprüche zur Prüfung · ${nf(e)} menschliche Zuordnungen bleiben unberührt`,
    vorschauOhneRahmen: "(Kästchen erst mit eingeschaltetem Rahmen)",
    vorschauLaedt: "Vorschau wird gerechnet …",
    festschreiben: "Bogenvorlage festschreiben",
    festschreibenFrage: "Bogenvorlage festschreiben? Danach hängen die Seiten dieser Blätter ihre Antworten an die gedruckten Fragen; menschliche Zuordnungen bleiben. „Zurückziehen“ stellt den Stand davor wieder her.",
    festgeschrieben: (n: number) => `Bogenvorlage festgeschrieben · ${nf(n)} Fragen`,
    angewandt: (fertig: number, gesamt: number) => `Vorlage angewandt: ${nf(fertig)} von ${nf(gesamt)} Seiten`,
    zurueckziehen: "Zurückziehen",
    zurueckziehenFrage: "Bogenvorlage zurückziehen? Die Fragenliste steht danach wie vor dem Festschreiben; nichts wird gelöscht.",
    zurueckgezogen: "Bogenvorlage zurückgezogen.",
    gespeichert: "gespeichert",
    speichert: "speichert …",
    veraltet: "Jemand anderes hat den Entwurf geändert. Deine letzte Änderung ist noch zu sehen, aber nicht gespeichert.",
    neuLaden: "Neu laden",
    fehlerSpeichern: "Nicht gespeichert",
    banner: (sicher: number, n: number) => `Ein Entwurf aus dem leeren Bogen liegt vor — ${nf(sicher)} von ${nf(n)} Fragen sicher.`,
    zurBogenvorlage: "Zur Bogenvorlage",
    gesperrtFragenliste: "aus der Bogenvorlage — dort bearbeiten",
    tasten: "J/K nächste/vorige Frage · Enter bestätigen · V verwerfen · B Kästchen wählen",
    schwaerzung: "Schwärzung",
  },
  fragen: {
    unterzeile:
      "Ohne Formularvorlage liest das Sehmodell zu jeder Antwort eine kurze Bezeichnung der gedruckten Frage — oft in mehreren Schreibweisen. Hier werden sie zu einer Fragenliste gebündelt. Nach „Übernehmen“ gruppiert die Auswertung nach diesen Fragen; später gelesene Seiten ordnet derselbe Abgleich zu. Was im Prüfen von Hand zugeordnet wurde, bleibt so.",
    leer: "Noch keine gedruckten Fragen erkannt",
    leerSub: "Die Liste entsteht aus gelesenen Seiten ohne Formularvorlage. Verfahren mit Vorlage brauchen sie nicht.",
    vorschlag: "Vorschlag — noch nicht gespeichert",
    gespeichert: (rev: number) => `Gespeichert · Stand ${rev}`,
    uebernommen: (am: string, von: string | null) => `Übernommen ${am}${von ? ` von ${von}` : ""}`,
    nichtUebernommen: "Noch nicht übernommen",
    neueSchreibweisen: "Neue Schreibweisen seit dem letzten Speichern",
    ungespeichert: "Ungespeicherte Änderungen",
    zahlen: (mit: number, zu: number, hand: number) => `${mit.toLocaleString("de-DE")} Antworten mit gedruckter Frage · ${zu.toLocaleString("de-DE")} zugeordnet · ${hand.toLocaleString("de-DE")} von Hand (bleiben)`,
    speichern: "Speichern",
    uebernehmen: "Übernehmen",
    speichernUndUebernehmen: "Speichern und übernehmen",
    neuVorschlagen: "Neu vorschlagen",
    neuVorschlagenFrage: "Die gespeicherte Liste verwerfen und neu aus den Schreibweisen vorschlagen?",
    neuVorschlagenText: "Zusammengelegte, getrennte, umbenannte und verworfene Fragen gehen verloren; der Vorschlag entsteht neu aus den gelesenen Schreibweisen. Das geht nur, solange die Liste nicht übernommen ist.",
    worum: "Worum geht es?",
    listeGeaendert: "Die Liste wurde inzwischen von jemand anderem geändert — Speichern meldet einen Konflikt.",
    gespeichertNichtUebernommen: (grund: string) => `Gespeichert, aber nicht übernommen: ${grund}`,
    nichtZugeordnet: (n: number) => `${n.toLocaleString("de-DE")} Antworten weichen von der übernommenen Liste ab — erneut übernehmen`,
    doppelt: (n: number) => `${n.toLocaleString("de-DE")} doppelt auf einer Seite (bleiben frei)`,
    ohnePlatz: (n: number, r: number) =>
      `${n === 1 ? "1 weitere Frage hat" : `${n.toLocaleString("de-DE")} weitere Fragen haben`} keinen Platz in der Liste (${r.toLocaleString("de-DE")} Antworten bleiben frei) — erst Fragen zusammenlegen`,
    personenbezugBehalten: (n: number, nichtUebernommen: boolean) =>
      `${n === 1 ? "Eine Frage bleibt" : `${n} Fragen bleiben`} personenbezogen — ihre Schreibweisen kamen aus einer personenbezogenen Frage (zusammengelegt oder abgetrennt).${nichtUebernommen ? " Gespeichert, aber nicht übernommen: bitte prüfen, dann erneut übernehmen." : ""} Lösen lässt sich der Haken jetzt an der Frage selbst.`,
    neuBerechnet: "Zahlen nach dem Speichern",
    neuBerechnetTitel: "Die Schreibweisen dieser Frage wurden hier geändert — Seiten, Optionen und Beispiele rechnet der Server beim Speichern neu.",
    mehrdeutig: "mehrdeutig",
    mehrdeutigTitel: "Steht auf vielen Seiten doppelt — wohl zwei gedruckte Fragen mit derselben Bezeichnung. Wird nicht automatisch zugeordnet; im Prüfen lassen sich die Antworten von Hand einer Frage geben.",
    gemischt: "gemischt",
    andereArt: " (andere Antwortart)",
    artAbweichend: (art: string) => `gilt als ${art} — die gewählte Art passt nicht zu den gelesenen Optionen`,
    skalenrichtung: "Skalenrichtung",
    details: "Details",
    verschiebenGefiltert: "Bei gewählter Charge nur unter den sichtbaren Fragen",
    verwerfenOrtlich: "Änderungen verwerfen",
    gespeichertToast: "Fragenliste gespeichert",
    uebernommenToast: (n: number, r: number) => `Übernommen: ${n} Fragen, ${r.toLocaleString("de-DE")} Antworten zugeordnet`,
    veraltet: "Jemand anderes hat die Liste inzwischen geändert — sie wurde neu geladen.",
    uebernehmenTitel: "Fragenliste übernehmen",
    uebernehmenText: (n: number) =>
      `${n} Fragen werden zur Fragenliste des Verfahrens (Schlüssel F1, F2, …). Jede passende Antwort bekommt ihre Frage; die Auswertung, Exporte und der Assistent gruppieren danach. Von Hand zugeordnete Antworten bleiben, wie sie sind. Erneutes Übernehmen nach Änderungen behält die Schlüssel.`,
    personenHinweis: (n: number) => `${n} als personenbezogen markierte Fragen: ihre Antworten werden ausgeblendet (wie „Personenbezug“ im Prüfen).`,
    regionen: (n: number, s: number) => `${n.toLocaleString("de-DE")} Antworten · ${s.toLocaleString("de-DE")} Seiten`,
    zugeordnet: (n: number) => `${n.toLocaleString("de-DE")} zugeordnet`,
    schreibweisen: (n: number) => (n === 1 ? "1 Schreibweise" : `${n} Schreibweisen`),
    neu: "neu",
    neueVariante: "neue Schreibweise",
    personenbezogen: "personenbezogen",
    verworfen: "verworfen",
    art: "Antwortart",
    artAuto: (a: string) => `automatisch (${a})`,
    arten: { freitext: "Freitext", zahl: "Zahl", ankreuz: "Ankreuzen", einfach: "Einfachwahl", mehrfach: "Mehrfachwahl", skala: "Skala", zeichnung: "Zeichnung" } as Record<string, string>,
    familie: { frei: "Text", kreuz: "Kreuze", zeichnung: "Zeichnung" } as Record<string, string>,
    richtung: { hoch_ist_gut: "hoch ist gut", niedrig_ist_gut: "niedrig ist gut" } as Record<string, string>,
    hoch: "Nach oben",
    runter: "Nach unten",
    umbenennen: "Umbenennen",
    vorschlagNehmen: (t: string) => `Vorschlag nehmen: „${t}“`,
    zusammenlegen: "Zusammenlegen mit …",
    zusammenlegenMit: "Diese Frage geht auf in",
    abtrennen: "Ausgewählte Schreibweisen abtrennen",
    alsPersonenbezogen: "Personenbezogen (Antworten ausblenden)",
    verwerfen: "Verwerfen (keine Frage)",
    wiederherstellen: "Wiederherstellen",
    beispiele: "Beispiele",
    optionen: "Optionen",
    hoehe: (y: number) => `Höhe ${Math.round(y * 100)} %`,
    alleChargen: "Alle Chargen",
    // E34 — „Sichere Fragen automatisch übernehmen“ (der Server entscheidet nach einer festen Regel)
    sicher: {
      schalter: "Sichere Fragen automatisch übernehmen",
      schalterTitel:
        "Fragen, deren Schreibweisen eindeutig zusammengehören, die genug Antworten auf mehreren Seiten haben, nicht nach der Person fragen und gegen die die KI nichts hat, übernimmt der Server selbst. Alles andere bleibt hier „offen — bitte prüfen“. Ausschalten nimmt nichts zurück.",
      stand: (fragen: number, offen: number) =>
        `${fragen === 1 ? "1 Frage" : `${nf(fragen)} Fragen`} automatisch übernommen, ${nf(offen)} offen`,
      am: (wann: string) => `zuletzt ${wann}`,
      nochNichts: (offen: number) => (offen ? `Noch nichts automatisch übernommen · ${nf(offen)} offen` : "Noch nichts automatisch übernommen"),
      laeuft: "Sichere Fragen werden übernommen …",
      wartet: "Wartet, bis die gespeicherte Liste übernommen ist — dann kommen neue sichere Fragen dazu.",
      sicherOffen: (n: number) => (n === 1 ? "1 davon ist sicher und kommt beim nächsten Lauf dazu" : `${nf(n)} davon sind sicher und kommen beim nächsten Lauf dazu`),
      aus: "Aus — der Server übernimmt keine Frage von selbst. Schon Übernommenes bleibt.",
      automatisch: "automatisch übernommen",
      uebernommenAm: (am: string) => `Automatisch übernommen ${am}`,
      automatischTitel:
        "Vom Server als sicher übernommen — kein Mensch hat sie seither geändert. Umbenennen, zusammenlegen, trennen und verwerfen gehen wie bei jeder Frage; danach gilt sie als von Hand.",
      offen: "offen — bitte prüfen",
      sicher: "sicher",
      sicherTitel: "Erfüllt die Regel „sicher“: kommt beim nächsten automatischen Lauf dazu (oder jetzt mit „Speichern und übernehmen“).",
      kiAnders: "KI gruppiert anders — bitte prüfen",
      gruende: {
        personenbezug: "möglicherweise personenbezogen",
        ki_anders: "KI gruppiert anders",
        wie_gespeichert: "ähnelt einer Frage der Liste (auch einer verworfenen)",
        mehrdeutig: "steht auf vielen Seiten doppelt (mehrdeutig)",
        seite_doppelt: "zwei Schreibweisen auf einer Seite",
        aehnlich: "nur ähnliche Schreibweisen",
        art: "Antwortart uneinheitlich",
        seiten: "nur 1 Seite",
        antworten: "zu wenige Antworten",
        platz: "kein Platz in der Liste",
      } as Record<string, string>,
      offenMitUebernehmen: (n: number) =>
        n === 1 ? "1 offene Frage wird dabei mit übernommen — bitte vorher prüfen." : `${nf(n)} offene Fragen werden dabei mit übernommen — bitte vorher prüfen.`,
      eingeschaltet: "Sichere Fragen werden automatisch übernommen — der Server prüft gleich.",
      ausgeschaltet: "Automatisches Übernehmen ist aus. Schon Übernommenes bleibt.",
    },
    // E34 — Anzahl und Bestand der Antworten (Zahlen vom Zählwerk)
    bestand: {
      zugeordnet: (z: number, g: number) => `${nf(z)} von ${nf(g)} Antworten einer Frage zugeordnet`,
      regel: (n: number) => `${nf(n)} automatisch sicher`,
      mensch: (n: number) => `${nf(n)} von Menschen geprüft`,
      offen: (n: number) => `${nf(n)} offen`,
      anzahl: (n: number) => (n === 1 ? "1 Antwort" : `${nf(n)} Antworten`),
      balken: (b: { gesamt: number; regel: number; mensch: number; offen: number }) =>
        `${nf(b.gesamt)} Antworten: ${nf(b.regel)} automatisch sicher, ${nf(b.mensch)} von Menschen geprüft, ${nf(b.offen)} offen`,
    },
    // E31 — „KI ordnet die Fragen“ (nur gedruckte Bezeichnungen gehen an das lokale Modell)
    ki: {
      knopf: "Mit KI ordnen",
      knopfTitel:
        "Das lokale Sprachmodell auf GEX44 fasst die Schreibweisen zu den gedruckten Fragen zusammen und schlägt einen sauberen Fragetext vor. Es sieht nur die gedruckten Bezeichnungen, nie Antworten der Kinder. Das Ergebnis ist ein Vorschlag — übernommen wird nichts von selbst.",
      wartetLauf: "KI ordnet die Fragen — wartet, bis der laufende Lauf fertig ist",
      wartet: "KI ordnet die Fragen — wartet auf die GPU",
      laeuft: (seit: string) => `KI ordnet die Fragen … läuft seit ${seit}`,
      fehlgeschlagen: "Die KI konnte die Fragen nicht ordnen — die Liste ohne KI bleibt bestehen.",
      titel: "Vorschlag der KI — bitte prüfen",
      zahlen: (schreibweisen: number, fragen: number) =>
        `${schreibweisen.toLocaleString("de-DE")} Schreibweisen → ${fragen.toLocaleString("de-DE")} Fragen`,
      ohneKi: (n: number) => `ohne KI: ${n.toLocaleString("de-DE")}`,
      korrigiert: (n: number) => (n === 1 ? "1 Korrektur der Prüfung" : `${n.toLocaleString("de-DE")} Korrekturen der Prüfung`),
      korrigiertTitel:
        "Der Server prüft jede Antwort der KI nach: jede Schreibweise genau einmal, nie zwei Fragen derselben Seite zusammen, Antwortarten wie beim Zusammenlegen von Hand, kein erfundener Text. Was er dabei geändert hat, ist hier gezählt.",
      nichtAktuell: "Seither kamen neue Schreibweisen dazu — „Mit KI ordnen“ macht einen neuen Vorschlag.",
      angewandt: "In der Liste angewandt",
      ansehen: (n: number) => `${n} Fragen ansehen`,
      anwenden: "In die Liste übernehmen",
      anwendenTitel:
        "Ersetzt die Liste unten durch den Vorschlag — noch nicht gespeichert. Verworfene Fragen bleiben; Feldschlüssel und eigener Text bleiben bei der Frage mit den meisten gemeinsamen Schreibweisen. Legt die KI zwei gespeicherte Fragen zusammen, geht eine in der anderen auf — das steht vor dem Übernehmen da.",
      anwendenFrage: "Den Vorschlag der KI in die Liste übernehmen?",
      anwendenUngespeichert: "Deine ungespeicherten Änderungen an der Liste werden dabei ersetzt.",
      // Review 01.10.: nie still — die Zahl kommt vom Server (GET …/fragen/ki, `aufgeloest`)
      aufgeloest: (n: number) =>
        n === 1
          ? "1 gespeicherte Frage mit eigenem Text oder Feldschlüssel geht dabei in einer anderen auf — ihr Text und ihr Feldschlüssel entfallen."
          : `${n.toLocaleString("de-DE")} gespeicherte Fragen mit eigenem Text oder Feldschlüssel gehen dabei in anderen auf — ihre Texte und Feldschlüssel entfallen.`,
      angewandtToast: "Vorschlag der KI in die Liste übernommen — bitte prüfen, dann speichern und übernehmen.",
      eingereihtToast: "KI ordnet die Fragen — das läuft im Hintergrund (siehe Aufträge).",
      aktuellToast: "Der Vorschlag der KI passt schon zu allen Schreibweisen.",
      fertigToast: (schreibweisen: number, fragen: number) =>
        `Vorschlag der KI ist da: ${schreibweisen.toLocaleString("de-DE")} Schreibweisen → ${fragen.toLocaleString("de-DE")} Fragen`,
      schreibweisen: (n: number, r: number) => `${n === 1 ? "1 Schreibweise" : `${n} Schreibweisen`} · ${r.toLocaleString("de-DE")} Antworten`,
      personenbezogen: "KI: personenbezogen",
      herkunft: {
        ergaenzt: "von der KI ausgelassen",
        getrennt: "vom Server getrennt",
        mehrdeutig: "mehrdeutig",
        einzeln: "allein",
        mensch: "aus der Liste",
      } as Record<string, string>,
    },
  },
  allgemein: {
    produkt: "Handschrift",
    laedt: "Lädt …",
    schliessen: "Schließen",
    abbrechen: "Abbrechen",
    speichern: "Speichern",
    zurueck: "Zurück",
    erneut: "Erneut versuchen",
    laedtNicht: "Das lädt gerade nicht.",
    nurAnsehen: "Nur ansehen — ändern darf, wer die Rolle Prüfer hat.",
    keinRecht: "Dafür fehlt dir das Recht.",
    weitere: "Weitere laden",
    grund: "Grund",
    grundHinweis: (min: number, max: number) => `${min}–${max} Zeichen, wird mit deinem Namen protokolliert.`,
    ohneCode: "ohne Code",
  },
  fehler: {
    umgefallen: "Hier ist etwas schiefgegangen. Die anderen Bereiche funktionieren weiter.",
    neuLaden: "Neu laden",
    nochmal: "Noch einmal",
    nichtGefunden: "Diese Seite gibt es nicht.",
    zurAuswertung: "Zur Auswertung",
    unbekannt: "Diesen Verweis gibt es nicht oder du hast keinen Zugriff.",
  },
  shell: {
    nav: "Hauptnavigation",
    auswertung: "Auswertung",
    karte: "Karte",
    pruefen: "Prüfen",
    themen: "Themen",
    exporte: "Exporte",
    suchen: "Suchen oder fragen",
    hell: "Helles Thema",
    dunkel: "Dunkles Thema",
    verfahrenWechseln: (V: string, titel: string) => `Verfahren wechseln, aktuell ${V} · ${titel}`,
    verfahrenWaehlen: "Verfahren wählen",
    verfahrenMenue: "Verfahren",
    alleVerfahren: "Alle Verfahren · Übersicht",
    anzeigename: "Anzeigename ändern",
    anzeigenameFeld: "Anzeigename",
    anzeigenameGespeichert: "Anzeigename gespeichert",
    verwaltung: "Verwaltung",
    abmelden: "Abmelden",
    tastenkuerzel: "Tastenkürzel",
    fuss: "Handschrift · LILASp",
    // E18: öffentlich mit Anmeldung + Turnstile, nicht mehr nur im VPN. GEX44 bleibt intern.
    fussZugang: "Nur mit LILASp-Anmeldung · KI lokal auf GEX44",
    live: (seq: number) => `Stand #${seq} · live`,
    stand: (seq: number) => `Stand #${seq}`,
    liveTitel: (seq: number) => `Zählstand ${seq} — aktualisiert sich selbst`,
  },
  band: {
    testdaten: "Testdaten — synthetische Bögen, keine echten Kinderdaten",
    gesperrt: "Echte Bögen gesperrt — erst nach AVV, DSFA und Klärung des GEX44-Betreibers",
    // E36: angenommene Antworten zählen sofort, auch aus noch offenen Bögen
    geprueft: (g: number, n: number) => `${g} von ${n} Bögen ganz geprüft — angenommene Antworten zählen schon mit`,
    katalog: "Themenkatalog noch nicht eingefroren — Themenzahlen folgen",
  },
  // Karte (E32, 05 §19; Eigentümer 01.10.: „können wir auf einer karte dann später auch die beiträge
  // anschalten?“) — screens/karte. Zahlen kommen nur vom Server; unter 5 Kindern „< 5“ (E14).
  karte: {
    titel: "Karte",
    bereich: "Karte der Stadtteile, Orte und Beiträge",
    listeStattKarte: "Dieselben Zahlen als Tabelle (Auswertung)",
    laedt: "Karte wird geladen …",
    grundkarteFehler: "Die Grundkarte lädt gerade nicht (Kachelserver nicht erreichbar).",
    ebenen: "Ebenen",
    ebenenUndFilter: "Ebenen & Filter",
    grundkarte: "Grundkarte",
    grundkarteHell: "hell",
    grundkarteDunkel: "dunkel",
    stadtteile: "Stadtteile",
    stadtteileHinweis: "Kinder je Stadtteil — bestätigte Ortsangaben, Wohnorte nur hier.",
    orte: "Orte",
    orteHinweis: "Öffentliche Orte des Ortsverzeichnisses.",
    beitraege: "Beiträge",
    beitraegeHinweis: "Was Kinder an öffentlichen Orten nennen — nur bestätigt, nie Wohnorte.",
    an: "an",
    aus: "aus",
    anschalten: (n: string) => `${n} anschalten`,
    ausschalten: (n: string) => `${n} ausschalten`,
    immer: "immer",
    laedtKurz: "lädt …",
    fehlerKurz: "lädt nicht",
    erneut: "Erneut versuchen",
    legendeWenige: "wenige",
    legendeViele: "viele Kinder",
    legendeKlein: "< 5 Kinder — Zahl verdeckt",
    legendeUngefaehr: "ungefähr (ohne Fläche)",
    legendeGruppe: { spiel: "Spiel & Sport", bildung: "Schule & Kita", gruen: "Grün & Wasser", sonst: "Sonstiges" },
    legendeOrtGruppe: "mehrere Orte — antippen",
    legendeBeitrag: "Ort mit Beiträgen",
    eingrenzen: "Eingrenzen",
    frage: "Frage",
    alleFragen: "Alle Fragen",
    thema: "Thema",
    alleThemen: "Alle Themen",
    zaehlen: "Stadtteile zählen",
    // kurz für das 284-px-Blatt; der volle Satz (ORTSBEZUG_TEXT.titel) steht im Titel
    ortsbezugKurz: { alle: "alle", ort: "genannte Orte", wohnort: "Wohnorte" },
    weitereFilter: "Aus der Auswertung",
    filterZuruecksetzen: "Filter zurücksetzen",
    filterAnzahl: (n: number) => (n === 1 ? "1 Filter" : `${n} Filter`),
    filterKurz: "Filter",
    // Bedienung
    hinein: "Hineinzoomen",
    heraus: "Herauszoomen",
    einpassen: "Ganze Kommune zeigen",
    // Leer
    leer: "Noch kein Ortsverzeichnis.",
    leerSub: "Ohne Stadtteile und Orte gibt es nichts zu zeigen — unter „Ortsverzeichnis → Laden“ holen.",
    zumOrtsverzeichnis: "Zum Ortsverzeichnis",
    keineBeitraege: "Noch keine bestätigten Ortsangaben an öffentlichen Orten.",
    // Blatt (Auswahl)
    blatt: "Auswahl",
    schliessen: "Schließen (Esc)",
    stadtteil: "Stadtteil",
    ungefaehr: "ungefähr — ohne Fläche",
    kinder: (wert: string) => (wert === "1" ? "1 Kind" : `${wert} Kinder`),
    wenigerAls5: "weniger als 5 Kinder",
    beitraegeZeigen: "Beiträge zeigen",
    beitraegeHier: "Beiträge an diesem Ort",
    gruppe: (n: number) => (n === 1 ? "1 Ort" : `${n} Orte`),
    gruppeZuGross: "Zu viele Orte auf einmal — weiter hineinzoomen.",
    hineinzoomen: "Hineinzoomen",
    sucht: "Beiträge werden gesucht …",
    laedtNicht: "Die Beiträge lassen sich gerade nicht laden.",
    keineHier: "Keine bestätigten Beiträge mit dieser Auswahl.",
    mehrLaden: "Weitere laden",
    mehrLaedt: "Lädt weitere …",
    bogen: "Bogen",
    wohnortHinweis: "Wohnorte zählen nur im Stadtteil — nie als Punkt.",
    kleinHinweis: "Unter 5 Kindern steht keine Zahl (Kleinzellen-Regel).",
    zurAuswertung: "In der Auswertung",
    // Einstiege von anderen Bildschirmen
    aufDerKarte: "Auf der Karte zeigen",
    aufDerKarteTitel: "Stadtteile, Orte und Beiträge auf der Karte",
  },
  // E34 — Bestand der Antworten über den Kennzahlen der Auswertung (Zahlen vom Zählwerk, GET …/auswertung/bestand)
  bestandBand: {
    titel: "Antworten",
    fragen: (n: number) => (n === 1 ? "1 Frage" : `${nf(n)} Fragen`),
    // E36: dieselbe Definition wie die Zählung („angenommene Antworten“, Vorgabe der Auswertung)
    hinweis:
      "Gezählt wird jede angenommene Antwort — automatisch sicher (geschlossene Antworten, zwei gleiche Lesungen) oder von Menschen geprüft —, auch wenn andere Antworten desselben Bogens noch offen sind. Offenes kommt nach der Prüfung dazu.",
    // E36: Kopfzahl = angenommene Antworten in gezählten Bögen (vom Server) — nur sie stehen in der Auswertung
    angenommen: (n: number) => `${nf(n)} angenommen und gezählt`,
    ausgeschlossen: (n: number) =>
      `${n === 1 ? "1 angenommene Antwort steht" : `${nf(n)} angenommene Antworten stehen`} in Bögen, die nicht zählen (Dublette, ausgeschlossen)`,
    ohneBogen: (seiten: number, antworten: number) =>
      `${seiten === 1 ? "1 Seite" : `${nf(seiten)} Seiten`} noch keinem Bogen zugeordnet${antworten > 0 ? ` — ${antworten === 1 ? "1 angenommene Antwort zählt" : `${nf(antworten)} angenommene Antworten zählen`} erst mit dem Bogen` : ""}`,
    zuDenBoegen: "Bögen bilden",
    ohneFragen: "Noch keine Frage übernommen — gruppiert wird erst nach der Fragenliste.",
    zurFragenliste: "Zur Fragenliste",
  },
  // Befehlssuche (Rückmeldung 01.10.: „search bar to also search for tools and functions and pages
  // and ai asking“) — components/app/Befehlssuche.tsx und befehle.ts.
  befehle: {
    titel: "Suche",
    feld: "Seiten, Werkzeuge, Codes oder eine Frage",
    platzhalter: "Seite, Werkzeug, Code oder Frage …",
    knopf: "Suchen",
    knopfTitel: "Seiten, Werkzeuge, Codes und Text suchen oder die KI fragen (F oder Strg+K)",
    liste: "Treffer",
    gruppeSeiten: "Seiten & Werkzeuge",
    gruppeCodes: "Codes & Text",
    gruppeKi: "KI-Assistent",
    oeffnen: (code: string) => `Öffnen: ${code}`,
    codeArt: "Verweis",
    inAntworten: (q: string) => `In Antworten suchen: „${q}“`,
    inAntwortenArt: "Fragebar",
    kiFragen: (q: string) => `KI fragen: „${q}“`,
    kiArt: "lokal, GEX44",
    ohneVerfahren: "Erst ein Verfahren wählen.",
    ohneRechtLesen: "Dafür fehlt dir das Recht in diesem Verfahren.",
    ohneRechtKi: "Dafür fehlt dir das Recht „KI-Assistent“.",
    keineTreffer: "Keine Seite und kein Werkzeug heißt so.",
    hilfe: "↑ ↓ wählen · Enter öffnen · Esc schließen",
    aktuell: "aktuell",
    laufStarten: "Lauf starten",
    kodieren: "Kodieren",
    rohdaten: "Rohdaten exportieren",
    orteLaden: "Ortsverzeichnis laden",
    karteBeitraege: "Beiträge auf der Karte",
    kiOeffnen: "KI-Assistent öffnen",
    alleVerfahren: "Alle Verfahren",
    verfahrenVerwalten: "Verfahren verwalten",
    ort: {
      seite: "Seite",
      auswertung: "Auswertung",
      karte: "Karte",
      pruefen: "Prüfen",
      themen: "Themen",
      orte: "Ortsverzeichnis",
      verfahren: "Verfahren",
      verwaltung: "Verwaltung",
      konto: "Konto",
      werkzeug: "Werkzeug",
    },
  },
  anmelden: {
    titel: "Anmelden",
    unterzeile: "Mit deiner Büro-Adresse. Du bekommst einen sechsstelligen Code per E-Mail.",
    email: "Deine LILASp-Adresse",
    emailPlatzhalter: "vorname@lilasp.de",
    domains: (d: string[]) => "Nur " + d.map((x) => "@" + x).join(" und "),
    bleiben: "Auf diesem Gerät angemeldet bleiben (7 Tage)",
    codeSchicken: "Code schicken",
    codeTitel: "Code eingeben",
    codeUnterzeile: "Wir haben dir einen sechsstelligen Code geschickt. Er gilt 10 Minuten.",
    ziffer: (i: number, n: number) => "Ziffer " + i + " von " + n,
    andereAdresse: "Andere Adresse",
    erneut: "Code erneut senden",
    erneutIn: (s: number) => `Erneut in ${s} s`,
    codeFehler: "Der Code stimmt nicht.",
    startFehler: "Der Code ließ sich nicht anfordern.",
    // Wortgleich aus /opt/lilasp-zeitplan/frontend/src/lib/texte.ts (Turnstile, E18).
    pruefungFehler: "Die Prüfung von Cloudflare ließ sich nicht laden.",
    zugang: "Nur mit LILASp-Anmeldung erreichbar.",
    gesendetAn: (email: string) => `Gesendet an ${email}`,
  },
  verfahren: {
    titel: "Verfahren",
    suche: "Verfahren suchen",
    ergebnis: (n: number, von: number) => `${n} von ${von}`,
    zuruecksetzen: "Zurücksetzen",
    kommune: "Kommune",
    jahr: "Jahr",
    stufe: "Stufe",
    boegen: "Bögen",
    kinder: "Kinder",
    geprueft: "geprüft",
    synthetisch: "synthetisch",
    echt: "echt",
    erklaerung: "Ein Verfahren ist eine Beteiligung in einer Kommune. Die Zahlen gelten für geprüfte Bögen.",
    anlegen: "Verfahren anlegen",
    keine: "Kein Verfahren passt zu diesen Filtern.",
    keineHinweis: "Filter lockern oder zurücksetzen.",
    keinZugriff: "Du hast noch auf kein Verfahren Zugriff.",
    keinZugriffHinweis: "Ein Admin gibt dir Zugriff in der Verwaltung.",
    stand: (n: number) => `Stand #${n}`,
    stufeEingang: "Eingang",
    stufePruefung: "Prüfung",
    stufeAuswertung: "Auswertung",
    oeffnen: "Öffnen",
    blattSchliessen: "Verfahrensübersicht schließen",
  },
  pruefen: {
    titel: "Prüfen",
    reiterWarteschlange: "Warteschlange",
    reiterBoegen: "Bögen bilden",
    reiterEingang: "Eingang",
    reiterLauf: "Lauf",
    reiterGold: "Gold",
    reiterFragen: "Fragenliste",
    // E35: Bogenvorlage aus dem leeren Bogen; die Zahl = offene Fragen des Entwurfs
    reiterBogenvorlage: "Bogenvorlage",
    reiterBogenvorlageOffen: (n: number) => `Bogenvorlage · ${nf(n)}`,
    reiterOrte: "Orte prüfen",
    reiterOrteOffen: (n: number) => `Orte prüfen · ${nf(n)}`,
    // E37: gefundene Zeichnungen, die das Modell nicht markiert hat; die Zahl = offene
    reiterZeichnungen: "Zeichnungen",
    reiterZeichnungenOffen: (n: number) => `Zeichnungen · ${nf(n)}`,
    band: (g: number, n: number) => `${g} von ${n} Bögen geprüft`,
    stichprobe: (g: number, soll: number, fehler: number) => `Stichprobe: ${g} von ${soll} geprüft · ${fehler} Fehler`,
    bestanden: "bestanden",
    nichtBestanden: "nicht bestanden",
    obergrenze: (p: string) => `Obergrenze 95 %: ${p} %`,
    stichprobeZiehen: "Stichprobe ziehen",
    stichprobeGezogen: (n: number) => `Stichprobe gezogen: ${n} Felder`,
    nurPruefer: "Prüfen darf, wer die Rolle Prüfer hat.",
    grund: "Grund",
    alleGruende: "Alle Gründe",
    status: { offen: "Offen", zurueckgestellt: "Zurückgestellt", erledigt: "Erledigt" },
    charge: "Charge",
    alleChargen: "Alle Chargen",
    konfidenz: (k: string) => `Konfidenz ${k}`,
    ampel: { rot: "Ampel rot", gelb: "Ampel gelb", gruen: "Ampel grün" },
    leer: "Alles geprüft. Die Zahlen in der Auswertung enthalten jetzt nur noch geprüfte Lesungen.",
    zurAuswertung: "Zur Auswertung",
    seitenLabel: (bogen: string, seite: number, region: string) => `Bogen ${bogen} · Seite ${seite} · Region ${region}`,
    ausschnitt: "Ausschnitt",
    ganzeSeite: "Ganze Seite",
    neueRegion: "Region",
    regionZurueckziehen: "Region zurückziehen",
    originalZeigen: "Original zeigen",
    originalAus: "Geschwärzt zeigen",
    ungeschwaerzt: "ungeschwärzt",
    originalFrage: "Warum brauchst du die ungeschwärzte Seite?",
    seiteDrehen: "Seite drehen",
    drehen: { 90: "90° rechts", 180: "180°", 270: "90° links" } as Record<90 | 180 | 270, string>,
    drehenBestaetigen: "Die Seite wird neu gerastert; noch nicht geprüfte Regionen dieser Seite werden neu gelesen.",
    gedreht: "Seite wird neu gerastert",
    goldMarkieren: "Als Goldseite markieren",
    goldMarkiert: "Goldseite markiert",
    zuVieleOriginale: "Zu viele Originale in dieser Stunde.",
    originalGesperrt: "Original gesperrt (Beleg gelöscht).",
    rahmenVeraltet: "Der Rahmen wurde inzwischen geändert — neu geladen.",
    rahmenEingefroren: "Der Bogen ist geprüft, der Rahmen ist eingefroren.",
    trotzdemAendern: "Trotzdem ändern …",
    rahmenGespeichert: "Rahmen gespeichert",
    regionAnlegen: "Neue Region",
    regionArt: "Art",
    regionFrage: "Frage",
    regionAngelegt: (c: string) => `Region angelegt · ${c}`,
    regionVeraltet: "Die Seite wurde inzwischen neu gerastert — neu geladen. Bitte die Region noch einmal aufziehen.",
    regionZeichnen: "Zieh auf der Seite ein Rechteck auf. Esc bricht ab.",
    ersetztDurch: "ersetzt durch",
    regionZurueckgezogen: "Region zurückgezogen",
    lesungen: "Lesungen A / N",
    nUebernehmen: "N übernehmen",
    woertlich: "Wörtlich",
    woertlichHinweis: "So wie das Kind geschrieben hat — Fehler bleiben stehen. * = Buchstabe unleserlich, [?] = Wort unleserlich.",
    zustand: "Zustand",
    verglichen: "Mit dem Ausschnitt verglichen",
    pflicht: "Pflichtfeld: bitte prüfen",
    pflichtOk: "✓ geprüft",
    lesefassung: "Lesefassung",
    frage: "Frage",
    ohneFrage: "ohne Frage",
    fragePruefen: "Frage prüfen",
    frageBestaetigt: "bestätigt",
    frageFrei: "frei",
    zuordnungStimmt: "Zuordnung stimmt",
    antwortwerte: "Antwortwerte",
    werteSpeichern: "Werte speichern",
    werteGespeichert: "Werte gespeichert",
    zeichnung: "Zeichnung",
    zeichnungBestaetigen: "Zeichnung bestätigen",
    zeichnungBestaetigt: "Zeichnung bestätigt",
    kind: "Kind",
    bogenOeffnen: "Bogen öffnen",
    nennungen: "Nennungen → Themen",
    imKodieren: "Im Kodieren öffnen",
    markierungen: "Markierungen",
    personenbezug: "Personenbezug (Name, Adresse, Gesundheit, Familie)",
    freigegeben: "Für Veröffentlichung freigegeben",
    nieFreigeben: "Personenbezogenes wird nie freigegeben.",
    bogen: "Bogen",
    bogenCodeOeffnen: (c: string) => `Bogen ${c} öffnen`,
    inhaltLoeschen: "Inhalt löschen (DSGVO) …",
    inhaltGeloescht: "Inhalt gelöscht. Kennung und Links bleiben.",
    unleserlich: "Unleserlich",
    leerMenue: "Leer …",
    zurueckstellen: "Zurückstellen",
    verwerfen: "Verwerfen",
    verwerfenTitel: "Änderungen verwerfen und zur Liste (Esc) — die Region bleibt, wie sie war",
    mehr: "Mehr ⋯",
    keineAntwort: "Keine Antwort — Region zurückziehen",
    keineAntwortFrage: "Die Region wird zurückgezogen und zählt danach nicht mehr als Antwort.",
    keineAntwortKnopf: "Region zurückziehen",
    geschlossen: "Kein Beleg offen. Wähl eine Zeile aus der Liste.",
    /** Nur mit Tastatur (hover:hover): der j/k-Hinweis zum Platzhalter. */
    geschlossenTasten: "j / k blättern.",
    wiederOeffnen: "Beleg wieder öffnen",
    entwurf: {
      speichert: "speichert …",
      gespeichert: (uhr: string) => `gespeichert · ${uhr}`,
      wieder: (uhr: string) => `Entwurf von ${uhr} wiederhergestellt`,
      veraltet: "Entwurf auf älterem Stand — verworfen",
      offline: "nicht gespeichert — offline",
      fehler: "nicht gespeichert",
      konflikt: "nicht gespeichert — Beleg inzwischen geändert",
      nichtGeladen: "Entwurf nicht geladen — Autosave aus",
      erneut: "erneut",
      werteErst: "Antwortwerte zuerst speichern",
      zeichnungErst: "Zeichnung zuerst bestätigen",
      verworfen: "Änderungen verworfen.",
      rueckgaengig: "Rückgängig",
    },
    uebernehmen: "Übernehmen",
    rotZuerst: "Rotes Feld zuerst prüfen",
    textBraucht: "Bei „Text“ braucht es einen Wortlaut.",
    uebernommen: (code: string, rev: number) => `Übernommen · ${code} · Rev ${rev}`,
    veraltet: "Jemand hat diesen Beleg inzwischen geändert — neu geladen.",
    tasten: "Tastenkürzel",
    tastenAus: "Tastenleiste ausblenden",
    tastenAn: "Tastenleiste zeigen",
    gold: {
      titel: "Goldseiten",
      fertig: "fertig",
      offen: "offen",
      blind: "Blindmodus: die KI-Lesung wird nicht gezeigt. Schreib ab, was du siehst.",
      speichern: "Gold-Lesung speichern",
      gespeichert: "Gold-Lesung gespeichert",
      keine: "Noch keine Goldseiten. Markiere eine Seite in der Warteschlange als Goldseite.",
      regionen: (n: number) => `${n} Regionen`,
      keineRegionen: "Auf dieser Seite gibt es keine Regionen zum Abschreiben.",
      geschlossen: "Keine Region offen. Wähl eine Region aus der Liste.",
    },
  },
  boegen: {
    einzel: "Einzelbogen",
    gruppe: "Gruppenbogen",
    kinder: "Kinder",
    signal: {
      neuer_bogen: "neuer Bogen erkannt",
      vorlage_seitenzahl: "Seitenzahl der Vorlage",
      uebliche_seitenzahl: "übliche Seitenzahl",
      frage_neustart: "Fragen beginnen neu",
      charge: "Beginn der Charge",
      name_neu: "neuer Name",
    } as Record<string, string>,
    sicher: "sicher",
    pruefen: "prüfen",
    unsicher: "unsicher",
    trennen: "✂ hier trennen",
    zusammenlegen: "⤒ mit vorherigem zusammenlegen",
    nurEineCharge: "Zusammenlegen geht nur innerhalb einer Charge.",
    bestaetigen: "Bestätigen",
    alleSicheren: "Alle sicheren bestätigen",
    angelegt: (codes: string[]) => `Bögen angelegt: ${codes.slice(0, 5).join(", ")}${codes.length > 5 ? " …" : ""}`,
    leer: "Keine offenen Bogenvorschläge. Neue entstehen, sobald ein Lauf Seiten gelesen hat.",
    unterzeile: "Erst die Bestätigung vergibt die Codes. Seiten, die zusammengehören, bilden einen Bogen.",
    // E36 (01 §2.9.5): eindeutige Vorschläge übernimmt der Server selbst
    automatisch: {
      schalter: "Eindeutige Bögen automatisch übernehmen",
      schalterTitel:
        "Der Server übernimmt einen Vorschlag nur, wenn er eindeutig ist: Einzelbogen mit der üblichen Seitenzahl, lückenlos, Namensfeld genau auf der ersten Seite, keine offene Seitenfrage. Alles andere bleibt hier stehen. Ausschalten nimmt nichts zurück.",
      uebernommen: (n: number) => (n === 1 ? "1 Bogen automatisch übernommen" : `${nf(n)} Bögen automatisch übernommen`),
      laenge: (n: number) => `übliche Seitenzahl: ${n}`,
      laengeOffen: (min: number) => `übliche Seitenzahl noch unbekannt — erst ${min} Bögen von Hand bestätigen`,
      laeuft: "wird gerade geprüft …",
      aus: "aus — jeder Bogen wird von Hand bestätigt",
      chipAutomatisch: "automatisch übernommen",
      gruppiertAm: (am: string) => `Gruppierung automatisch übernommen am ${am} (eindeutiger Vorschlag) — trennen über „Zurückziehen“`,
      eindeutig: "eindeutig — kommt automatisch",
      grund: {
        gruppenbogen: "Gruppenbogen",
        getrennt: "von Hand getrennt",
        laenge_unbekannt: "Seitenzahl unbekannt",
        seitenzahl: "Seitenzahl weicht ab",
        luecke: "Seiten nicht lückenlos",
        kein_name: "kein Namensfeld auf Seite 1",
        name_spaeter: "Namensfeld auf späterer Seite",
        ki_anders: "KI sieht andere Grenze",
        grenze_unklar: "nächste Seite ohne Namensfeld",
        seite_unklar: "Seite unklar (leer, doppelt, nicht fertig gelesen)",
        seitenfrage: "offene Seitenfrage",
      } as Record<BogenOffenGrund, string>,
      seitenzahlText: (ist: number, soll: number) => `${ist} ${ist === 1 ? "Seite" : "Seiten"} statt ${soll}`,
    },
  },
  eingang: {
    titel: "Eingang",
    e12: "Echte Bögen sind noch nicht freigegeben (AVV, DSFA, Betreiber GEX44). Bis dahin nur synthetische Verfahren.",
    chargen: "Chargen",
    neueCharge: "Neue Charge",
    chargeBearbeiten: "Charge bearbeiten",
    schule: "Schule / Einrichtung",
    klasse: "Klasse",
    erhebungsdatum: "Erhebungsdatum",
    standort: "Standort",
    keinStandort: "keiner",
    neuerStandort: "+ Neuer Standort",
    standortCode: "Code",
    standortName: "Name",
    standortKurz: "Kurzname",
    standortAngelegt: (n: string) => `Standort angelegt: ${n}`,
    formular: "Formular",
    ohneVorlage: "ohne Vorlage (frei)",
    vorlageImportieren: "Vorlage importieren …",
    vorlagenAngelegt: (keys: string[]) => `Vorlagen angelegt: ${keys.join(", ")}`,
    vorlageKaputt: "Die Datei ist kein gültiges Vorlagen-JSON (erwartet: {\"vorlagen\": […]}).",
    notiz: "Notiz",
    chargeAngelegt: "Charge angelegt",
    chargeGespeichert: "Charge gespeichert",
    maskeGesetzt: "Namensfeld geschwärzt",
    maskeFehlt: "Namensfeld: fehlt",
    // E19 — gelernte Charge-Schwärzung (Konsens der automatisch erkannten Namensfelder).
    maskeErkannt: (mit: number, gesamt: number) => `Namensfeld automatisch erkannt · ${nf(mit)} von ${nf(gesamt)} Seiten`,
    maskeTeilweise: (mit: number, gesamt: number) => `Namensfeld nur auf ${nf(mit)} von ${nf(gesamt)} Seiten erkannt — Rest bitte schwärzen`,
    maskeNichtErkannt: "Namensfeld nicht erkannt — bitte schwärzen",
    maskeZusaetzlich: "dazu automatisch erkannt",
    maskeErkanntTitel: (mit: number, gesamt: number) =>
      `Gedruckte Namensfelder (Name, Unterschrift, Adresse …) standen auf ${nf(mit)} von ${nf(gesamt)} Seiten. Die gelernte Stelle wird auf allen Seiten dieser Charge geschwärzt; Klasse, Alter und Schule bleiben lesbar.`,
    maskeNichtErkanntTitel:
      "Auf diesen Seiten wurde kein gedrucktes Namensfeld gefunden (z. B. gemalte Karten). Die Seiten bleiben gehalten, bis jemand schwärzt oder sie ausdrücklich freigibt.",
    // E20 — Schwärzung im Verfahren aus: keine Mahnungen, nur ein ruhiger Satz.
    schwaerzungAus: "Schwärzung für dieses Verfahren aus",
    schwaerzungAusChip: "Schwärzung aus",
    schwaerzungAusText:
      "Die Seiten gehen ohne Schwärzung an das lokale Sehmodell auf GEX44. Ein von Hand gezeichnetes Namensfeld wird trotzdem geschwärzt. Umschalten unter Verwaltung → Verfahren.",
    schwaerzungAusNachzug: "Einige Seiten stammen noch aus der Zeit mit Schwärzung. Der Server gibt sie von selbst weiter; sie gehen mit dem nächsten Lauf mit.",
    hochladen: "PDFs hochladen",
    schwaerzen: "Namensfeld schwärzen",
    ablegen: "PDF-Scans hier ablegen",
    waehlen: "PDF-Scans hierher ziehen oder auswählen",
    wartet: "wartet",
    laedt: "lädt",
    aufgenommen: (c: string) => `aufgenommen als ${c}`,
    vorhanden: (c: string) => `schon vorhanden: ${c}`,
    zuGross: "Die Datei ist zu groß (höchstens 300 MB). Größere Scans über den Eingangsordner.",
    keinPdf: "Das ist keine PDF-Datei.",
    abgebrochen: "abgebrochen",
    seiten: (n: number) => `${n} Seiten`,
    neu: "neu",
    keineDokumente: "Noch keine PDFs in dieser Charge.",
    keineChargen: "Noch keine Chargen. Eine Charge ist ein Stapel Bögen aus einer Schule oder Einrichtung.",
    ordner: "Aus dem Eingangsordner",
    ordnerLeer: "Im Eingangsordner dieses Verfahrens liegen keine PDFs.",
    bekannt: "bekannt",
    importieren: "Importieren",
    importErgebnis: (neu: string[], dubletten: string[], fehler: number) =>
      [neu.length ? `Neu: ${neu.join(", ")}` : "", dubletten.length ? `schon vorhanden: ${dubletten.join(", ")}` : "", fehler ? `${fehler} Fehler` : ""]
        .filter(Boolean)
        .join(" · ") || "Nichts importiert",
    importChargeWaehlen: "Erst eine Charge wählen — oder im Auswahlfeld „Neue Charge anlegen“.",
    importLaeuft: "Importiere …",
    chargeWaehlen: "Charge wählen …",
    chargeNeuOption: "+ Neue Charge anlegen …",
    importHinweisDateien: "Dateien anhaken, die importiert werden sollen.",
    importHinweisCharge: "Zu welcher Charge (Schule, Klasse) gehören die Bögen? Wählen oder neu anlegen — eine Charge je Schule.",
    importHinweisBereit: "Bereit. Nach dem Import werden die Seiten gerastert und geschwärzt — den Fortschritt zeigt „Aufträge“ oben in der Kopfleiste.",
    importWeiter: "Rastern und Schwärzen laufen jetzt im Hintergrund — den Fortschritt zeigt „Aufträge“ oben in der Kopfleiste.",
    gehalten: "Gehaltene Seiten",
    gehaltenUnterzeile: "Diese Seiten gehen erst an das Sehmodell, wenn ihr Namensfeld geschwärzt oder die Seite ausdrücklich freigegeben ist.",
    keineGehaltenen: "Keine gehaltenen Seiten.",
    erkennen: "Namensfelder erkennen",
    erkennenTitel: "Namensfelder automatisch erkennen",
    erkennenText:
      "Sucht auf den gerasterten Seiten dieses Verfahrens nach gedruckten Namensfeldern (Name, Vorname, Unterschrift, Adresse, Telefon …) und schwärzt diese Stelle — bei mehrseitigen Bögen auch auf den Folgeseiten derselben Charge.",
    erkennenLokal:
      "Die Erkennung läuft auf diesem Server mit Tesseract, ohne KI: keine Seite geht dafür an das Sehmodell, und kein erkannter Text wird gespeichert — nur die Lage der Rechtecke. Klasse, Alter und Schule bleiben lesbar.",
    erkennenRest: "Seiten ohne erkanntes Namensfeld bleiben gehalten. Den Fortschritt zeigt „Aufträge“ oben in der Kopfleiste.",
    erkennenStarten: "Erkennung starten",
    erkennenErgebnis: (eingereiht: number, chargen: number) =>
      eingereiht || chargen
        ? [eingereiht ? `${nf(eingereiht)} ${eingereiht === 1 ? "Seite" : "Seiten"} zur Erkennung eingereiht` : "", chargen ? `${nf(chargen)} ${chargen === 1 ? "Charge" : "Chargen"} neu gelernt` : ""]
            .filter(Boolean)
            .join(" · ")
        : "Nichts zu tun — die Erkennung ist auf allen Seiten aktuell.",
    seiteAnsehen: "Seite ansehen",
    seiteAnsehenGrund: "Gehaltene Seite ansehen (Maske)",
    seiteSchwaerzen: "Schwärzen",
    freigeben: "Ohne Schwärzung freigeben …",
    freigebenWarnung: "Die Seite geht ungeschwärzt an das Sehmodell auf GEX44. Die Freigabe wird mit deinem Namen protokolliert.",
    freigegeben: "Seite freigegeben",
    neuLesen: "Die Seite wird beim nächsten Lauf neu gelesen.",
    maskeGespeichert: "Schwärzung gespeichert",
    dateiname: "Datei",
    groesse: "Größe",
    datum: "Datum",
  },
  masken: {
    titelCharge: "Namensfeld für die ganze Charge schwärzen",
    subCharge: "Zieh ein Rechteck über das Namensfeld der ersten Seite. Es gilt für alle Seiten dieser Charge.",
    titelSeite: "Diese Seite schwärzen",
    subSeite: "Zieh Rechtecke über alles, was ein Kind erkennbar macht.",
    grund: "Schwärzung zeichnen (Maskeneditor)",
    erstNachRastern: "Erst nach dem Rastern möglich.",
    hoechstens: "Höchstens 12 Rechtecke",
    rechteck: (i: number) => `Schwärzung ${i}`,
    entfernen: "Entfernen",
    anzahl: (n: number) => `${n} von 12 Rechtecken`,
    erkanntHinweis: "Automatisch erkannt; eigene Rechtecke kommen hinzu.",
    erkanntLegende: "Gestrichelt: automatisch erkannte Namensfelder — werden immer geschwärzt, hier nicht änderbar.",
    erkanntEbene: (n: number) => `${nf(n)} automatisch ${n === 1 ? "erkanntes Namensfeld" : "erkannte Namensfelder"} (gestrichelt, nicht änderbar)`,
    nichtErkannt: "Auf diesen Seiten wurde kein gedrucktes Namensfeld automatisch erkannt.",
    hilfe: "Ziehen zeichnet · Rechteck ziehen verschiebt · Griffe ändern die Größe · Tab wählt, Pfeile bewegen (Shift ×10), Alt+Pfeile ändern die Größe, Entf löscht.",
  },
  auftraege: {
    titel: "Aufträge",
    knopf: (offen: number, fehler: boolean) =>
      ["Aufträge", offen ? `${offen} ${offen === 1 ? "läuft" : "laufen"}` : "gerade nichts in Arbeit", fehler ? "Fehler in den letzten 24 Stunden" : ""].filter(Boolean).join(" · "),
    hochladen: "Hochladen",
    import: "Import",
    importLaeuft: (n: number) => (n === 1 ? "1 Datei aus dem Eingangsordner wird importiert …" : `${n} Dateien aus dem Eingangsordner werden importiert …`),
    aufbereitung: "Aufbereitung",
    leseLauf: "Lese-Lauf (GPU)",
    nachDemLesen: "Nach dem Lesen",
    gpuFremd: "Die GPU ist gerade für ein anderes Verfahren belegt — dieser Lauf wartet, bis sie frei ist.",
    exporte: "Exporte in Erstellung",
    exportLaeuft: (n: number) => (n === 1 ? "1 Export wird geschrieben und gegengeprüft …" : `${n} Exporte werden geschrieben und gegengeprüft …`),
    fehler: "Fehler (24 h)",
    fehlerZeile: (n: number, uhr: string) => `${n} fehlgeschlagen · zuletzt ${uhr}`,
    fehlerHinweisCpu: "Betroffene Dokumente tragen im Eingang einen roten Hinweis.",
    fehlerHinweisGpu: "Im Lauf lassen sich die Seiten erneut lesen.",
    fehlerHinweisKaestchen: "Diese Seiten bleiben ohne Kästchenmessung; die Antworten des Modells zählen unverändert.",
    fehlerHinweisDublette: "Die Hinweise „Doppelt gescannt?“ bleiben, wie sie waren; in Prüfen lassen sie sich von Hand beantworten.",
    // E34
    fehlerHinweisFragen: "Die Fragenliste bleibt, wie sie war; in Prüfen → Fragenliste lässt sie sich von Hand übernehmen.",
    zurFragenliste: "Fragenliste",
    // E35: die drei Schritte der Bogenvorlage — ihre Wirkung (Entwurf, Vorschau) liegt in Prüfen → Bogenvorlage.
    bogenvorlage: "Bogenvorlage",
    zurBogenvorlage: "Bogenvorlage",
    fehlerHinweisBogenvorlage: "Der Entwurf bleibt, wie er war; in Prüfen → Bogenvorlage lässt sich das Lesen neu anstoßen.",
    // E21: ein gescheiterter Import schreibt nichts (eine Transaktion am Ende).
    fehlerHinweisOrte: "Das Ortsverzeichnis bleibt unverändert. Unter „Ortsverzeichnis → Laden“ erneut versuchen.",
    // E21h: CPU-Schritt `ortsteilfeld` (Ortsteil-Feld der Umfrage nach dem Lesen)
    fehlerHinweisOrtsteilfeld: "Diese Seiten bleiben ohne Zuordnung des Ortsteil-Felds; „Orte zuordnen“ im Lauf holt sie nach.",
    ortsverzeichnis: "Ortsverzeichnis",
    zumOrtsverzeichnis: "Zum Ortsverzeichnis",
    leer: "Gerade läuft nichts.",
    zuletzt: (text: string, n: number, uhr: string) => `Zuletzt fertig: ${text} · ${n} um ${uhr}`,
    zumEingang: "Eingang",
    zumLauf: "Lauf",
    zumPruefen: "Prüfen",
    zuExporten: "Exporte",
    laeuft: (n: number) => `${n} ${n === 1 ? "läuft" : "laufen"}`,
    laeuftSeit: (uhr: string) => `läuft seit ${uhr}`,
    wartet: (n: number) => `${n} ${n === 1 ? "wartet" : "warten"}`,
    fehlerChip: (n: number) => `${n} Fehler`,
    fertigUm: (uhr: string) => `fertig um ${uhr}`,
    restWenig: "noch weniger als eine Minute",
    restMinuten: (m: number) => (m <= 1 ? "noch etwa eine Minute" : `noch etwa ${m} Minuten`),
    restStunden: (h: number) => (h <= 1 ? "noch etwa eine Stunde" : `noch etwa ${h} Stunden`),
    modell: (m: string) => `Modell ${m}`,
    stoppt: "Wird nach der aktuellen Seite angehalten …",
    laedtNicht: "Der Stand der Aufträge lädt gerade nicht.",
    hochladenFertig: "hochgeladen",
  },
  lauf: {
    titel: "Lauf",
    gpu: "GEX44",
    erreichbar: "erreichbar",
    nichtErreichbar: "nicht erreichbar",
    // Grau ohne Haken hiess bisher "nicht geladen", las sich aber wie "geladen".
    sehmodell: (an: boolean) => (an ? "Sehmodell geladen" : "Sehmodell nicht geladen"),
    textmodell: (an: boolean) => (an ? "Textmodell geladen" : "Textmodell nicht geladen"),
    gpuHinweis: "Ein Lauf belegt die GPU. Andere Anwendungen auf GEX44 sind währenddessen langsamer.",
    offen: (n: number) => `${n} offen`,
    gehalten: (n: number) => `${n} Seiten gehalten (ohne Schwärzung)`,
    // E20: Schwärzung aus — die Seiten werden nachgezogen, niemand muss etwas tun.
    gehaltenNachzug: (n: number) => `${nf(n)} ${n === 1 ? "Seite wird" : "Seiten werden"} ohne Schwärzung nachgezogen und ${n === 1 ? "geht" : "gehen"} mit dem nächsten Lauf mit.`,
    zuGehaltenen: "Zu den gehaltenen Seiten",
    starten: "Lauf starten",
    schritte: "Schritte",
    lesen: "Seiten lesen (Sehmodell)",
    nachlesen: "Ausschnitte nachlesen",
    nachlesenHinweis: "nur wenn auf dem Server eingeschaltet",
    lesefassung: "Lesefassung schreiben",
    verorten: "Orte zuordnen (KI-Vorschläge)",
    verortenHinweis: "wählt nur unter Orten des Ortsverzeichnisses; Vorschläge prüft ein Mensch",
    verortenOhneOrte: "erst ein Ortsverzeichnis laden",
    zumOrtsverzeichnis: "Zum Ortsverzeichnis",
    orteFehlen: "Für „Orte zuordnen“ fehlt das Ortsverzeichnis. Erst unter „Ortsverzeichnis“ laden.",
    kodieren: "Themen zuordnen (KI-Vorschläge)",
    nurNeu: "Nur neue Seiten",
    gestartet: "Lauf gestartet",
    laeuftSchon: "Es läuft schon ein Lauf.",
    maskenOffen: (n: number) => `${n} Seiten sind noch gehalten (ohne Schwärzung). Erst schwärzen oder freigeben.`,
    gexAus: "GEX44 ist nicht eingerichtet.",
    laeuft: "Lauf läuft",
    jeSeite: (s: number) => `ca. ${s} s je Seite`,
    regionen: (n: number) => `${n} Regionen gelesen`,
    regionenLabel: "Regionen gelesen",
    zurPruefungLabel: "zur Prüfung",
    gepruefteBoegenLabel: "Bögen geprüft",
    fehlerLabel: "Seiten mit Fehler",
    pruefquote: (z: number, g: number, n: number) => `${z} zur Prüfung · ${g} von ${n} Bögen geprüft`,
    fehler: (n: number) => `${n} Fehler`,
    gestartetVon: (wer: string, uhr: string) => `gestartet von ${wer} um ${uhr}`,
    pause: (uhr: string) => `Pause bis ${uhr} — eine andere Anwendung nutzt die GPU. Der Lauf macht danach von selbst weiter.`,
    pauseNetz: (uhr: string) => `Pause bis ${uhr} — GEX44 ist gerade nicht erreichbar. Der Lauf macht danach von selbst weiter.`,
    // E28: GEX44 antwortet mit einem anderen Modell — der Lauf wartet und versucht es wieder (1 → 15 min).
    pauseModell: (uhr: string) => `Pause bis ${uhr} — GEX44 antwortet mit einem anderen Modell als festgelegt. Der Lauf wartet, bis das festgelegte Modell wieder antwortet, und macht dann von selbst weiter.`,
    stoppt: "Wird nach der aktuellen Seite angehalten …",
    anhalten: "Anhalten",
    anhaltenBestaetigen: "Die Seite, die gerade gelesen wird, wird noch fertig gelesen.",
    angehalten: "Lauf wird angehalten",
    leerlauf: "Kein Lauf aktiv",
    wartend: "Wartende Arbeit",
    nichtsOffen: "Nichts offen — alle Seiten sind gelesen.",
    modelle: "Geladene Modelle",
    keineModelle: "Kein Modell geladen",
  },
  zeichnungen: {
    // E37 (SPEC 03 §9.7.9) — Prüfen → „Zeichnungen“: was die Seitenanalyse gefunden hat und das Modell nicht als
    // Zeichnung markiert hat. Gezählt wird erst nach „Übernehmen“.
    titel: "Gefundene Zeichnungen",
    erklaerung: "Die Seitenanalyse sucht auf jeder Seite, was ein Kind gezeichnet hat — auch dort, wo das Sehmodell keine Zeichnung markiert hat. Gezählt wird eine Zeichnung erst, wenn du sie hier übernimmst.",
    postenTitel: "Zeichnung gefunden, die das Modell nicht markiert hat",
    filter: { offen: "offen", uebernommen: "übernommen", verworfen: "verworfen", alle: "alle" } as Record<string, string>,
    leer: "Keine gefundenen Zeichnungen in dieser Auswahl.",
    zahlen: (o: number, u: number, v: number) => `${nf(o)} offen · ${nf(u)} übernommen · ${nf(v)} verworfen`,
    seite: (dok: string, n: number) => `${dok} · Seite ${nf(n)}`,
    karte: "Karte gezeichnet",
    zeichnung: "Zeichnung",
    kartenwert: (w: number) => `Kartenwert ${nf(Math.round(w * 100))} %`,
    ki: "Das Sehmodell sagt",
    kiArt: { karte: "Karte / Plan", bild: "Bild", diagramm: "Skizze", keine: "keine Zeichnung" } as Record<string, string>,
    kiOffen: "Noch nicht vom Sehmodell angesehen.",
    kiHinweis: {
      personenbezug_moeglich: "Eine Beschriftung sah nach einem Namen oder einer Anschrift aus und wurde weggelassen.",
      wohnort: "Auf der Karte steht ein Wohnwort (z. B. „zuhause“) — Orte nur von Hand zuordnen, als Stadtteil.",
      keine_zeichnung: "Das Sehmodell hält das nicht für eine Zeichnung.",
      beschriftung_verworfen: "Nicht jede gelesene Beschriftung bestand die Prüfung.",
    } as Record<string, string>,
    gesehenVon: (art: string | null) => (art ? `liegt in einer Region „${art}“ des Modells` : "vom Modell nicht erfasst"),
    dinge: "Was ist gezeichnet?",
    beschriftungen: "Beschriftungen auf der Karte",
    beschriftungenHilfe: "Angehakte Beschriftungen werden eine Textantwort (aus der Zeichnung); mit Ortsverzeichnis schlägt die KI danach Orte vor — bestätigt in „Orte prüfen“.",
    orteVorschau: "Mögliche Orte:",
    uebernehmen: "Als Zeichnung übernehmen",
    verwerfen: "Keine Zeichnung",
    uebernommen: "Übernommen — die Zeichnung zählt jetzt.",
    verworfen: "Verworfen.",
    imDokument: "Im Dokument zeigen",
    laedtNicht: "Diese Zeichnung lässt sich gerade nicht laden.",
    nurPruefer: "Gefundene Zeichnungen sieht nur, wer prüfen darf.",
  },
  seitenanalyse: {
    // E37 — Karte im Lauf und Band in der Auswertung (nur Zahlen des Servers)
    titel: "Seitenanalyse",
    seiten: (a: number, g: number) => `${nf(a)} von ${nf(g)} gelesenen Seiten analysiert`,
    zeichnungen: (n: number, k: number) => `${nf(n)} ${n === 1 ? "Zeichnung" : "Zeichnungen"} gefunden · ${nf(k)} ${k === 1 ? "Karte" : "Karten"}`,
    uebersehen: (n: number) => `${nf(n)} vom Modell nicht als Zeichnung markiert`,
    uebersehenLabel: "vom Modell nicht markiert",
    offen: (n: number) => `${nf(n)} in Prüfen offen`,
    marken: (nah: number, aus: number) => `Marken: ${nf(nah)} an Ankreuzfragen · ${nf(aus)} anderswo`,
    grundlage: (lb: number, sa: number, oh: number) => `Druck abgezogen: ${nf(lb)} leerer Bogen · ${nf(sa)} Seitenart · ${nf(oh)} ohne`,
    ki: (n: number, k: number) => `KI: ${nf(n)} gedeutet, ${nf(k)} Karten`,
    kiAus: "KI-Deutung aus (SEITENANALYSE_AKTIV)",
    nachziehen: "Analysieren",
    nachziehenTitel: "Gelesene Seiten ohne aktuelle Analyse einreihen",
    nachziehenErgebnis: (n: number) => (n ? `${nf(n)} ${n === 1 ? "Seite" : "Seiten"} eingereiht.` : "Alles schon analysiert."),
    zuPruefen: "Zu den Zeichnungen",
    bandHinweis: "Gezählt wird eine gefundene Zeichnung erst nach der Prüfung.",
  },
  kaestchen: {
    // Prüfen (Sektion + Ebene über der Seite)
    titel: "Kästchen",
    zeile: (angekreuzt: number, anzahl: number) => `Kästchen: ${nf(angekreuzt)} von ${nf(anzahl)} angekreuzt`,
    unklar: (n: number) => `${nf(n)} unklar`,
    modell: (n: number) => `Modell: ${nf(n)}`,
    modellOhne: "Modell: —",
    ausstehend: "Messung steht noch aus — „Kästchen messen“ läuft im Hintergrund.",
    keine: "Keine Kästchen gefunden. Gemessen werden gedruckte Kästchen (eckig, selten rund), die zu zweit oder mehr in einer Reihe oder Spalte stehen.",
    vergleich: {
      gleich: "stimmt mit Modell",
      abweichend: "weicht vom Modell ab",
      unklar: "Kreuz unklar",
      nicht_vergleichbar: "nicht vergleichbar",
      ohne_kaestchen: "keine Kästchen",
    } as Record<KaestchenVergleich, string>,
    zustand: TX_KAESTCHEN_ZUSTAND,
    fuellgrad: (f: number) => `${prozent(f)} gefüllt`,
    // E33: `merkmal` (nur Vorlagenmessung) hängt den Grund an — außer bei ausgemalt, das sagt der Text schon.
    box: (nr: number, label: string | null, zustand: KaestchenZustand, f: number, ausgemalt = false, merkmal: KaestchenMerkmal | null = null) =>
      `Kästchen ${nr}${label ? ` „${label}“` : ""}: ${
        ausgemalt ? (zustand === "leer" ? "ausgemalt (Korrektur, zählt nicht)" : "ausgemalt — einziges Zeichen, bitte entscheiden") : TX_KAESTCHEN_ZUSTAND[zustand]
      }, ${prozent(f)} gefüllt${merkmal && !ausgemalt && merkmal !== "korrektur" && merkmal !== "ausgemalt_allein" ? ` (${TX_KAESTCHEN_MERKMAL[merkmal]})` : ""}`,
    // Ausgemalt (hs-kaestchen-2): ganz ausgemalt heißt meist „verschrieben“ — es zählt das andere Kreuz.
    ausgemaltKorrektur: "Ein ganz ausgemaltes Kästchen gilt als Korrektur und zählt nicht; gewählt ist das angekreuzte.",
    ausgemaltAllein: "Das einzige Zeichen ist ein ganz ausgemaltes Kästchen — Korrektur oder Antwort? Bitte selbst entscheiden.",
    liste: "Gemessene Kästchen — antippen zeigt das Kästchen auf der Seite",
    uebernehmen: "Messung übernehmen",
    uebernehmenHinweis: "Schreibt die gemessenen Kreuze als Antwort (Methode „Pixel“) und schließt den Beleg.",
    aktuellGleich: "Die gespeicherte Antwort stimmt mit der Messung überein.",
    zuordnungKeine: "Kästchen und Optionen ließen sich nicht eindeutig zuordnen — bitte die Antwort selbst eintragen.",
    ebene: (n: number) => `${nf(n)} gemessene Kästchen eingezeichnet`,
    rest: "angekreuzt außerhalb jeder Region",
    // Lauf: Gütekarte
    karteTitel: "Kästchenmessung",
    karteUnterzeile: "Eine zweite, pixelgenaue Lesung der Ankreuzfragen — lokal auf diesem Server, ohne KI. Sie zählt nie von selbst mit.",
    seiten: (gemessen: number, gelesen: number) => `${nf(gemessen)} von ${nf(gelesen)} gelesenen Seiten gemessen`,
    seitenOffen: (n: number) => `${nf(n)} ${n === 1 ? "wartet" : "warten"}`,
    seitenOhne: (n: number) => `${nf(n)} ohne Messung`,
    seitenOhneTitel: "Gelesen, aber (noch) nicht gemessen — z. B. ein fehlgeschlagener Auftrag (Aufträge) oder eine Seite ohne messbares Bild.",
    gefundenLabel: "Kästchen gefunden",
    angekreuztLabel: "angekreuzt",
    unklarLabel: "unklar",
    modellLabel: "gleich wie das Modell",
    menschLabel: "gleich wie geprüfte Antworten",
    keineQuote: "—",
    modellBasis: (gleich: number, abweichend: number) => `${nf(gleich)} gleich · ${nf(abweichend)} abweichend`,
    abdeckung: (a: number) => `verglichen: ${prozent(a)} der Fragen mit Kästchen`,
    nurAnzahl: (gleich: number, abweichend: number) =>
      `ohne Zuordnung nur Anzahl: ${nf(gleich)} gleich · ${nf(abweichend)} abweichend (nicht in der Quote)`,
    menschBasis: (gleich: number, verglichen: number) => `${nf(gleich)} von ${nf(verglichen)} Regionen`,
    abweichungen: (n: number) => (n === 1 ? "1 Abweichung offen" : `${nf(n)} Abweichungen offen`),
    zurueckgestellt: (n: number) => `${nf(n)} zurückgestellt`,
    abweichungenPruefen: "Abweichungen prüfen",
    keineAbweichung: "Keine offene Abweichung.",
    ausserhalb: (angekreuzt: number, seiten: number) =>
      `${nf(angekreuzt)} ${angekreuzt === 1 ? "Kreuz" : "Kreuze"} außerhalb jeder Region auf ${nf(seiten)} ${seiten === 1 ? "Seite" : "Seiten"}`,
    ausserhalbSeiten: "Seiten ansehen:",
    ausserhalbSeite: (dok: string, n: number) => `${dok} S. ${nf(n)}`,
    ausserhalbHinweis: "Dort eine Region über die Frage ziehen; Bogen bestätigen erledigt den Hinweis.",
    restSeite: (n: number) => `${nf(n)} ${n === 1 ? "Kreuz" : "Kreuze"} außerhalb jeder Region auf dieser Seite (gepunktet) — dort eine Region ziehen.`,
    restListe: "Kästchen außerhalb jeder Region",
    ohneKaestchen: (n: number) => `${nf(n)} ${n === 1 ? "Ankreuzfrage" : "Ankreuzfragen"} ohne erkennbare Kästchen`,
    messen: "Kästchen messen",
    messenTitel: "Gelesene Seiten ohne aktuelle Messung einreihen",
    // E33 (Rahmen): Vorlagen je Seitenart aus den Scans lernen, dann an den bekannten Plätzen messen
    vorlagenLernen: "Vorlagen lernen",
    vorlagenLernenTitel: "Aus den Scans lernen, wo auf jeder Formularseite die Kästchen stehen (lokal, ohne KI)",
    vorlagenErgebnis: (n: number) => (n ? "Vorlagen werden gelernt — danach wird jede Seite neu gemessen." : "Nichts Neues zu lernen — die Seiten sind seit dem letzten Lernen gleich."),
    rahmenAktiv: "Gemessen an gelernten Vorlagen; Seiten ohne Vorlage misst der blinde Finder.",
    rahmenSchatten: "Vorlagen laufen zur Probe mit (Schattenbetrieb): gezählt, aber nicht wirksam — es gilt der blinde Finder.",
    rahmenSeiten: (ohne: number, ausrichtung: number) =>
      `${nf(ohne)} ${ohne === 1 ? "Seite" : "Seiten"} ohne gelernte Vorlage · ${nf(ausrichtung)} nicht ausgerichtet`,
    quelleZeile: (vorlage: number, blind: number) => `${nf(vorlage)} Fragen an der Vorlage gemessen · ${nf(blind)} blind`,
    quelleVorlage: "Gemessen an der gelernten Vorlage dieser Seitenart.",
    merkmal: TX_KAESTCHEN_MERKMAL,
    messenErgebnis: (n: number) => (n ? `${nf(n)} ${n === 1 ? "Seite" : "Seiten"} zum Messen eingereiht` : "Nichts zu tun — alle gelesenen Seiten sind gemessen."),
    jeDokument: "Je Dokument",
    spalten: { dokument: "Dokument", seiten: "Seiten", kaestchen: "Kästchen", angekreuzt: "angekreuzt", unklar: "unklar", abweichend: "Abw." },
    fassung: (f: string) => `Fassung ${f}. Die Schwellen sind nur auf synthetischen Seiten geeicht — vor Aussagen über echte Bögen auf Goldseiten nachprüfen.`,
    leer: "Noch keine gelesenen Seiten.",
    laedtNicht: "Die Kästchenmessung lädt gerade nicht.",
  },
  dokument: {
    // Dokumentansicht /d/:dok und /d/:dok/s/:n (E26): die vom Server gerasterten Seiten, kein PDF im Browser.
    titel: "Dokument",
    seite: (n: number) => `Seite ${nf(n)}`,
    seiteVon: (n: number, gesamt: number) => `Seite ${nf(n)} von ${nf(gesamt)}`,
    seiten: (n: number) => `${nf(n)} ${n === 1 ? "Seite" : "Seiten"}`,
    alleSeiten: "Alle Seiten",
    vorige: "Vorige Seite (←)",
    naechste: "Nächste Seite (→)",
    seiteFeld: "Seite",
    seiteFeldHilfe: (gesamt: number) => `Seitenzahl 1 bis ${nf(gesamt)}, Eingabetaste springt`,
    leiste: "Seiten des Dokuments",
    vorschau: (n: number) => `Seite ${nf(n)} öffnen`,
    vorschauAktuell: (n: number) => `Seite ${nf(n)} (offen)`,
    ebenen: "Einblenden",
    regionen: "Regionen",
    kaestchen: "Kästchen",
    kaestchenKeine: "Auf dieser Seite sind keine Kästchen gemessen.",
    // E37: Ebene „Elemente“ (Seitenanalyse) — was auf der Seite steht, nach Art gefärbt
    elemente: "Elemente",
    elementeAusstehend: "Diese Seite ist noch nicht analysiert.",
    elementeLaedtNicht: "Die Elemente lassen sich gerade nicht laden.",
    elementeKeine: "Auf dieser Seite hat die Seitenanalyse nichts gefunden.",
    elementeEbene: (n: number) => `${nf(n)} ${n === 1 ? "gefundenes Element" : "gefundene Elemente"}`,
    elementLegende: { marke: "Marke", schrift: "Schrift", zeichnung: "Zeichnung", karte: "Karte", sonstiges: "Sonstiges" } as Record<string, string>,
    elementOffen: (n: number) => `${nf(n)} vom Modell nicht als Zeichnung markiert — in Prüfen offen`,
    elementOhneDruck: "Ohne leeren Bogen: gedruckte Linien und Text können als Element erscheinen.",
    karteGezeichnet: "Karte gezeichnet",
    kaestchenLaedtNicht: "Die Kästchen lassen sich gerade nicht laden.",
    buehne: (n: number) => `Seite ${nf(n)}. Doppeltippen, Strg+Rad oder Plus/Minus zoomen, wischen oder Pfeiltasten blättern.`,
    gibtsNicht: "Dieses Dokument gibt es nicht oder du hast keinen Zugriff.",
    seiteGibtsNicht: "Diese Seite gibt es nicht oder du hast keinen Zugriff.",
    keineSeiten: "Dieses Dokument hat noch keine Seiten — es wird noch gerastert.",
    keinBild: "noch kein Bild",
    bildWeg: "Bild nicht verfügbar",
    neu: "neu",
    charge: "Charge",
    datei: "Datei",
    stand: "Stand",
    // Angaben zur Seite
    seiteInfo: "Diese Seite",
    seitentyp: "Seitentyp",
    bearbeitung: "Bearbeitung",
    schwaerzung: "Schwärzung",
    bogen: "Bogen",
    ohneBogen: "keinem Bogen zugeordnet",
    regionenZahl: (n: number) => `${nf(n)} ${n === 1 ? "Region" : "Regionen"}`,
    ohneRegionen: "Auf dieser Seite gibt es keine Regionen.",
    dublette: "Doppelt gescannt?",
    dubletteVon: (code: string, n: number) => `gleicht ${code} · Seite ${nf(n)}`,
    dubletteUnbekannt: "gleicht einer anderen Seite",
    hinweise: "Hinweise",
    regionTippen: "Eine Region antippen zeigt, was zu ihr gehört.",
    region: (b: string | null) => (b ? `Region ${b}` : "Region"),
    regionTitel: (b: string | null, art: string) => `${b ? `Region ${b}` : "Region"}, ${art}`,
    belegOeffnen: "Beleg öffnen",
    imPruefen: "In Prüfen öffnen",
    imDokument: "Im Dokument",
    imDokumentTitel: "Diese Seite in der Dokumentansicht öffnen (alle Seiten, Zoom)",
    zurueckgezogen: "zurückgezogen",
    personenbezug: "Personenbezug",
    ohneLesung: "noch nicht gelesen",
    legende: { text: "Text", leer: "leer / keine Angabe", unklar: "unleserlich / durchgestrichen", zeichnung: "Zeichnung", name: "Namensfeld" },
    // Original
    original: "Original-PDF",
    originalGross: "Das Original ist die hochgeladene Scan-Datei, ungeschwärzt und oft sehr groß (bis etwa 70 MB). Auf dem Telefon besser im WLAN laden — zum Ansehen reichen die Seiten hier.",
    seitentypLabel: {
      fragebogen: "Fragebogen",
      freitext: "Freitext",
      zeichnung: "Zeichnung",
      deckblatt: "Deckblatt",
      leer: "Leerseite",
      sonstiges: "Sonstiges",
    } as Record<string, string>,
    seitentypOffen: "noch nicht bestimmt",
    statusLabel: {
      neu: "wird gerastert",
      gerastert: "gerastert",
      gehalten: "gehalten (Schwärzung offen)",
      bereit: "bereit zum Lesen",
      gelesen: "gelesen",
      lesen_offen: "Lesen unvollständig",
      ausgeschlossen: "ausgeschlossen",
      fehler: "Fehler",
    } as Record<string, string>,
    maskeLabel: {
      offen: "offen",
      erkannt: "Namensfeld automatisch geschwärzt",
      nicht_noetig: "Schwärzung im Verfahren aus",
      freigegeben: "ohne Schwärzung freigegeben",
      charge: "Charge-Schwärzung",
      vorlage: "Vorlagen-Schwärzung",
      manuell: "von Hand geschwärzt",
    } as Record<string, string>,
    artLabel: { text: "Text", ankreuz: "Ankreuzen", skala: "Skala", zahl: "Zahl", zeichnung: "Zeichnung", name: "Namensfeld", sonstiges: "Sonstiges" } as Record<string, string>,
  },
  exporte: {
    // E36: Zahlen und Listen nur aus angenommenen Antworten (wie die Vorgabe der Auswertung)
    nurAngenommen: "Nur angenommene Antworten",
    nurAngenommenHilfe: "Zahlen und Listen wie die Auswertung: jede angenommene Antwort, auch aus noch offenen Bögen.",
    // E21 (05 §15.7): Ortsspalten nur in Verfahren mit Ortsverzeichnis.
    ortsspalten:
      "Mit Ortsspalten: Stadtteil, Ortsbezug, Ort, Ortsart, Länge, Breite — nur bestätigte Angaben; Koordinaten nur für öffentliche Orte, Wohnorte nur als Stadtteil.",
    namensnennung: "Die Namensnennung der Kartendaten (z. B. © OpenStreetMap-Mitwirkende, ODbL) steht im Blatt „Info“, in der CSV als Spalte „Kartendaten“, im ZIP in LIESMICH.txt.",
  },
  orte: {
    titel: "Ortsverzeichnis",
    unterzeile: "Stadtteile und benannte Orte dieses Verfahrens — nur öffentliche Daten. Das Sprachmodell wählt beim „Orte zuordnen“ nur unter diesen Einträgen.",
    menue: "Ortsverzeichnis",
    reiter: { uebersicht: "Übersicht", orte: "Orte", stadtteile: "Stadtteile", schulen: "Schulen", laden: "Laden" },
    leer: "Noch kein Ortsverzeichnis.",
    leerSub: "Unter „Laden“ die amtlichen Stadtteile hochladen oder die Orte der Kommune aus OpenStreetMap holen.",
    zumLaden: "Ortsverzeichnis laden",
    stand: {
      titel: "Stand",
      orte: "Orte",
      strassen: "Straßen",
      nichtOeffentlich: "nicht öffentlich",
      genau: "Stadtteile (Fläche)",
      ungefaehr: "Stadtteile (ungefähr)",
      aliase: "andere Namen",
      schulen: "Schulen verknüpft",
      schulenAutomatisch: (n: number) => `davon ${nf(n)} automatisch`,
      vorgeschlagen: "Vorschläge offen",
      bestaetigt: "Zuordnungen bestätigt",
      wohnort: (n: number) => `davon ${nf(n)} als Wohnort`,
    },
    // E21h (05 §16.7): Wohnort je Kind aus dem Ortsteil-Feld (Kinderzahlen, unter 5 „< 5“)
    ortsteilfeld: {
      titel: "Ortsteil-Feld",
      unterzeile: "Wohnort je Kind aus dem handschriftlichen Feld „Ortsteil“ der Umfrage — bestätigt, je Kind ein Wert.",
      bezirk: "Kinder mit Stadtteil",
      ausserhalb: "außerhalb der Stadt",
      unklar: "unklar (widersprüchlich)",
      ohne: "ohne Stadtteil",
      offen: "Vorschläge offen",
      offenUnklar: (n: number) => `davon ${nf(n)} unklar`,
      zumPruefen: "Ortsteil-Felder prüfen",
      leer: "Noch keine bestätigten Ortsteil-Felder.",
    },
    quellen: "Quellen",
    quelleArt: { osm: "OpenStreetMap", datei: "Datei", db: "Datenbank", eigen: "eigene Ebene", hand: "von Hand", kopie: "Kopie" } as Record<string, string>,
    quelleKurz: { eigen: "eigen", hand: "Hand", osm: "OSM", naeherung: "genähert" } as Record<string, string>,
    nichtInGebrauch: "nicht mehr in Gebrauch",
    abgerufen: (d: string) => `abgerufen ${d}`,
    karte: "Karte",
    karteLeer: "Noch keine Flächen oder Punkte für eine Karte.",
    strassenZeigen: "Straßen zeigen",
    legendeGenau: "Stadtteil (Fläche)",
    legendeUngefaehr: "Stadtteil (ungefähr)",
    legendeGruppe: { spiel: "Spielen und Sport", bildung: "Schulen und Kitas", gruen: "Grün und Wasser", sonst: "Weitere Orte" } as Record<string, string>,
    // Reiter Orte
    suche: "Ort suchen",
    alleArten: "Alle Arten",
    alleStadtteile: "Alle Stadtteile",
    ohneStadtteil: "ohne Stadtteil",
    alleQuellen: "Alle Quellen",
    inaktive: "Deaktivierte zeigen",
    spalten: { name: "Name", art: "Art", stadtteil: "Stadtteil", quelle: "Quelle", aliase: "andere Namen" },
    keineTreffer: "Kein Ort passt zu dieser Suche.",
    anlegen: "Ort anlegen",
    bearbeiten: "Bearbeiten",
    name: "Name",
    art: "Art",
    oeffentlich: "öffentlich",
    oeffentlichHinweis: "Nicht öffentliche Orte (z. B. Privatgelände) sehen nur Menschen mit dem Recht „Ortsverzeichnis“; sie tragen nie Koordinaten in Exporte.",
    nichtOeffentlich: "nicht öffentlich",
    aktiv: "aktiv",
    deaktiviert: "deaktiviert",
    stadtteilFest: "Stadtteil",
    stadtteilAuto: "automatisch (aus der Lage)",
    lage: "Lage",
    lageLaenge: "Länge (lon)",
    lageBreite: "Breite (lat)",
    lageVon: "Lage von Ort übernehmen",
    lageHinweis: "Dezimalgrad WGS84, z. B. 9,4123 / 53,1112 — oder die Lage eines anderen Orts übernehmen.",
    lageFehlt: "Bitte Länge und Breite angeben oder einen Ort wählen, dessen Lage übernommen wird.",
    alias: "anderer Name",
    aliasNeu: "Anderen Namen hinzufügen",
    aliasPlatzhalter: "z. B. Weichelsee",
    aliasVergeben: "Diesen Namen gibt es hier schon.",
    aliasEntfernen: (a: string) => `„${a}“ entfernen`,
    angelegt: (n: string) => `Ort angelegt: ${n}`,
    gespeichert: "Gespeichert",
    zielUnbekannt: "Dieser Ort oder Stadtteil gehört nicht (mehr) zu diesem Verfahren.",
    strasseHinweis: "Straßen zählen nie als Ort — nur über ihren Stadtteil.",
    // Reiter Stadtteile
    keineStadtteile: "Noch keine Stadtteile.",
    keineStadtteileSub: "Die amtlichen Stadtteile/Ortschaften als GeoJSON hochladen (Laden → Datei, Ebene „Stadtteile“). Bis dahin lassen sich Stadtteile aus Ortspunkten nähern.",
    genau: "Fläche",
    ungefaehr: "ungefähr",
    ungefaehrHinweis: "Ungefähre Stadtteile haben keine Fläche; Orte gehören zum nächstgelegenen Punkt. Sie stehen überall mit „(ungefähr)“.",
    anzahlOrte: (n: number) => `${nf(n)} ${n === 1 ? "Ort" : "Orte"}`,
    ausOrt: "Stadtteil aus Ort anlegen",
    ausOrtHinweis: "Ein Ortsteil-Punkt (z. B. ein Weiler) wird zum ungefähren Stadtteil.",
    stadtteilAngelegt: (n: string) => `Stadtteil angelegt: ${n}`,
    umbenennen: "Umbenennen",
    // Reiter Schulen
    schulenUnterzeile: "Die Schule einer Charge ist ein starker Hinweis für „beim Spielplatz“ oder „an der Schule“. Passt ihr Name eindeutig zu einer Schule oder Einrichtung im Verzeichnis, wird sie automatisch verknüpft; sonst hier den Ort bestätigen. Eine Wahl hier wird nie automatisch überschrieben.",
    automatisch: "automatisch",
    automatischTitel: "Aus dem Schulnamen der Charge eindeutig zugeordnet — noch von keinem Menschen bestätigt. Bestätigen oder einen anderen Ort wählen.",
    keineSchulen: "Noch keine Chargen mit Schule.",
    chargen: (n: number) => `${nf(n)} ${n === 1 ? "Charge" : "Chargen"}`,
    vorschlag: "Vorschlag",
    bestaetigen: "Bestätigen",
    andererOrt: "Anderen Ort wählen",
    lageUnbekannt: "Lage unbekannt",
    lageUnbekanntSetzen: "Ohne Ort",
    nichtGesetzt: "noch nicht verknüpft",
    // Reiter Laden
    osmTitel: "Aus OpenStreetMap laden",
    osmText: "Holt Stadtteile, Ortsteile, Spielplätze, Schulen, Grün, Gewässer und Straßennamen einer Kommune über Overpass. Overpass sieht dabei nur den getippten Namen der Kommune bzw. ihre Relationsnummer — nie Text aus den Bögen.",
    osmLizenz: "OSM-Daten stehen unter der ODbL; Karte, Ortsverzeichnis und Exporte tragen danach die Namensnennung.",
    gemeinde: "Kommune",
    gemeindePlatzhalter: "z. B. Rotenburg (Wümme)",
    gemeindeSuchen: "Suchen",
    gemeindeKeine: "Keine Kommune mit diesem Namen gefunden.",
    gemeindeTreffer: (name: string, schluessel: string | null, kreis: string | null) => [name, kreis, schluessel ? `AGS ${schluessel}` : null].filter(Boolean).join(" · "),
    relation: (id: number) => `Relation ${id}`,
    umfang: "Was laden?",
    osmLaden: "Laden",
    osmLaeuft: "Das Ortsverzeichnis wird geladen — den Fortschritt zeigt diese Seite und „Aufträge“ oben in der Kopfleiste.",
    /** Ersatz, falls eine 502 `overpass_aus` ohne Satz ankommt — sonst gilt der Satz des Servers. */
    overpassAus: "OpenStreetMap antwortet gerade nicht — bitte in einer Minute noch einmal versuchen.",
    // Suche läuft (Vorfall 01.10.: nur eine dünne Linie, dann nach einer Minute „Keine Verbindung“)
    sucheBand: "OpenStreetMap wird gefragt",
    sucheLaeuft: (s: number) => `Frage OpenStreetMap … ${s} s`,
    sucheLaeuftLange: "antwortet ein Server nicht, wird der nächste gefragt",
    /** Die Suche hat eine Gesamtfrist (Server: OVERPASS_SUCHE_FRIST_S) — das Band läuft ehrlich auf sie zu. */
    sucheFrist: (max: number) => `höchstens ${max} s`,
    nochEinmal: "Noch einmal",
    // Stand des jüngsten Imports (GET …/orte/import)
    importLaeuftTitel: {
      osm: "OpenStreetMap wird geladen",
      datei: "Datei wird übernommen",
      kopie: "Ortsverzeichnis wird kopiert",
      db: "Tabelle wird übernommen",
    } as Record<"osm" | "datei" | "kopie" | "db", string>,
    importFertig: "Import fertig",
    importFehlgeschlagen: "Import fehlgeschlagen",
    importWartet: "wartet auf einen freien Platz",
    importNaechsterVersuch: (uhr: string, versuch: number, max: number) => `Nächster Versuch um ${uhr} (${versuch} von ${max}).`,
    osmPhase: {
      grenze: "Gemeindegrenze holen",
      bezirke: "Stadtteile holen",
      ortspunkte: "Ortsteile und Weiler holen",
      spiel_sport: "Spielplätze & Sport holen",
      schulen_kitas: "Schulen & Kitas holen",
      gruen_wasser: "Parks, Wald & Gewässer holen",
      laeden: "Läden holen",
      kirchen_kultur: "Kirchen, Bibliotheken & Bahnhof holen",
      strassen: "Straßen holen",
      orte: "Stadtteile, Orte und Plätze holen",
      lesen: "Zeilen aus der Datenbank lesen",
      schreiben: "Ins Ortsverzeichnis schreiben",
    } as Record<string, string>,
    /** Was GERADE geschieht (Zeile unter dem Band: „38 % · 3 von 8 · Spielplätze & Sport werden geholt“). */
    phaseJetzt: {
      grenze: "Gemeindegrenze wird geholt",
      bezirke: "Stadtteile werden geholt",
      ortspunkte: "Ortsteile und Weiler werden geholt",
      spiel_sport: "Spielplätze & Sport werden geholt",
      schulen_kitas: "Schulen & Kitas werden geholt",
      gruen_wasser: "Parks, Wald & Gewässer werden geholt",
      laeden: "Läden werden geholt",
      kirchen_kultur: "Kirchen, Bibliotheken & Bahnhof werden geholt",
      strassen: "Straßen werden geholt",
      orte: "Stadtteile, Orte und Plätze werden geholt",
      lesen: "Zeilen werden gelesen",
      schreiben: "wird ins Ortsverzeichnis geschrieben",
    } as Record<string, string>,
    /** „38 % · 3 von 8 · Spielplätze & Sport werden geholt“ — Prozent = erledigte Schritte. */
    importZeile: (prozent: number, erledigt: number, gesamt: number, jetzt: string) => `${prozent} % · ${erledigt} von ${gesamt} · ${jetzt}`,
    zeilenGelesen: (n: number, ca: number | null) => (ca ? `${nf(n)} von etwa ${nf(ca)} Zeilen gelesen` : `${nf(n)} Zeilen gelesen`),
    importZahl: {
      orte_neu: "neue Orte",
      orte_geaendert: "aktualisiert",
      orte_deaktiviert: "nicht mehr da (inaktiv)",
      bezirke: "Stadtteile",
      strassen: "Straßen",
      aliase: "andere Namen",
      schulen_verknuepft: "Schulen verknüpft",
      zeilen: "Zeilen gelesen",
      ohne_geometrie: "ohne Geometrie übersprungen",
    } as Record<string, string>,
    importKeineAenderung: "Keine Änderung — das Verzeichnis war schon auf dem Stand.",
    dateiTitel: "Eigene Ebene hochladen",
    dateiText: "GeoJSON (FeatureCollection, WGS84). Amtliche Stadtteile als Flächen, Spielplatz- oder Schulkataster als Punkte oder Flächen, die Gemeindegrenze. Andere Formate vorher umwandeln: ogr2ogr -f GeoJSON -t_srs EPSG:4326 ziel.geojson quelle.gpkg",
    dateiWaehlen: "GeoJSON hierher ziehen oder auswählen",
    dateiAblegen: "GeoJSON hier ablegen",
    dateiZuGross: "Die Datei ist zu groß (höchstens 20 MB).",
    dateiLaedt: "lädt",
    vorschau: (anzahl: number, crs: string, geom: string) => `${nf(anzahl)} Objekte · ${geom} · ${crs}`,
    felder: "Felder",
    feld: "Feld",
    beispiele: "Beispiele",
    ebene: "Ebene",
    ebenen: { bezirke: "Stadtteile/Ortschaften (Flächen)", orte: "Orte (Punkte oder Flächen)", grenze: "Gemeindegrenze" } as Record<string, string>,
    titelFeld: "Titel der Quelle",
    titelPlatzhalter: "z. B. Gemarkungen LGLN 2026",
    titelHinweis: "Ein erneutes Hochladen mit derselben Ebene und demselben Titel ersetzt die Einträge (gleiche Kennungen).",
    namensfeld: "Namensfeld",
    artWahl: "Art der Orte",
    artVorgabe: "Eine Art für alle",
    artAusFeld: "Art aus einem Feld",
    artfeld: "Artfeld",
    artZuordnung: "Werte → Art",
    artZuordnungHinweis: "Gezeigt sind alle Werte des Feldes; nicht zugeordnete Werte bekommen die Vorgabe-Art.",
    artZuordnungViele: "Das Feld hat mehr als 50 verschiedene Werte — gezeigt sind Beispiele; nicht zugeordnete Werte bekommen die Vorgabe-Art.",
    namensnennungFeld: "Namensnennung",
    namensnennungHinweis: "Pflicht bei „sonstige“ — z. B. amtliche Geodaten: „© GeoBasis-DE/LGLN 2026“. Steht auf Karte und in jedem Export.",
    lizenz: "Lizenz",
    lizenzen: { eigen: "eigene Daten", "ODbL-1.0": "aus OpenStreetMap abgeleitet (ODbL)", sonstige: "sonstige" } as Record<string, string>,
    lizenzHinweis: "Stammt die Datei aus OpenStreetMap (z. B. osm.planet_osm_point), bitte „ODbL“ wählen — dann trägt sie die Namensnennung.",
    importieren: "Importieren",
    importLaeuft: "Die Datei wird übernommen — den Fortschritt zeigt diese Seite und „Aufträge“ oben in der Kopfleiste.",
    andereDatei: "Andere Datei",
    kopieTitel: "Aus einem anderen Verfahren übernehmen",
    kopieText: "Kopiert das Ortsverzeichnis (Stadtteile, Orte, andere Namen, Quellen samt Namensnennung) — sinnvoll für eine zweite Erhebung in derselben Kommune.",
    kopieVon: "Verfahren",
    kopieWaehlen: "Verfahren wählen …",
    kopieren: "Übernehmen",
    kopieLaeuft: "Das Ortsverzeichnis wird kopiert — den Fortschritt zeigt diese Seite und „Aufträge“ oben in der Kopfleiste.",
    // E29 (05 §18): „Aus Datenbank laden“ — unter „Eigene Ebene hochladen“
    dbTitel: "Aus Datenbank laden",
    dbText: "Liest eine Tabelle oder Sicht mit Geometrie aus einer Datenquelle (PostGIS, nur lesend) — dann dieselbe Zuordnung wie beim Hochladen. Nur öffentliche Ebenen (Stadtteile, Spielplätze, Schulen …), nie Daten von Kindern.",
    dbKeineQuelle: "Noch keine Datenquelle. Ein Admin legt sie unter „Verwaltung → Datenquellen“ an.",
    dbZurVerwaltung: "Zu den Datenquellen",
    dbQuelle: "Datenquelle",
    dbQuelleNichtBereit: "Passwort neu eingeben (Verwaltung)",
    dbDatenbank: "Datenbank",
    dbDatenbankenLaden: "Datenbanken werden gefragt …",
    dbKeineDatenbank: "Keine erlaubte Datenbank erreichbar.",
    dbNichtErreichbar: (n: number) => `${nf(n)} weitere erlaubt, aber nicht erreichbar`,
    dbOhnePostgis: "ohne PostGIS",
    dbTabelle: "Tabelle oder Sicht",
    dbTabellenLaden: "Tabellen werden gelesen …",
    dbKeineTabelle: "In dieser Datenbank gibt es keine Tabelle mit Geometrie, die dieser Zugang lesen darf.",
    dbTabelleSuchen: "Tabelle suchen …",
    dbMehrTabellen: "Es gibt noch mehr — bitte die Suche eingrenzen.",
    dbTabelleInfo: (typ: string, srid: number, zeilen: number | null, sicht: boolean) =>
      [typ, srid ? `EPSG:${srid}` : "ohne Bezugssystem", zeilen !== null ? `~${nf(zeilen)} Zeilen` : null, sicht ? "Sicht" : null].filter(Boolean).join(" · "),
    dbVorschauLaden: "Vorschau wird gelesen …",
    dbVorschau: (anzahl: number, crs: string, geom: string, quelle: string) => `${quelle} · ${nf(anzahl)} Zeilen · ${geom || "—"} · ${crs}`,
    dbZuViele: (max: number) => `Die Tabelle hat mehr als ${nf(max)} Zeilen mit Geometrie — bitte eine Sicht mit weniger Zeilen wählen.`,
    dbMax: (max: number) => `Übernommen werden höchstens ${nf(max)} Zeilen; leere Geometrien werden übersprungen.`,
    dbUebernehmen: "Übernehmen",
    dbAndereTabelle: "Andere Tabelle",
    dbLaeuft: "Die Tabelle wird übernommen — den Fortschritt zeigt diese Seite und „Aufträge“ oben in der Kopfleiste.",
    dbNurLesen: "Alle mit dem Recht „Ortsverzeichnis“ dürfen aktive Datenquellen nutzen — nur lesend.",
    nurAnsehen: "Nur ansehen — ändern darf, wer das Recht „Ortsverzeichnis“ hat.",
  },
  verortung: {
    titel: "Orte prüfen",
    unterzeile: "Vorschläge, welcher Ort oder Stadtteil in einer Antwort gemeint ist. Gezählt wird erst, was hier bestätigt ist. Wohnorte zählen nur als Stadtteil.",
    leer: "Keine offenen Ortsvorschläge.",
    leerSub: "Neue entstehen mit dem Lauf-Schritt „Orte zuordnen“.",
    filterSicherheit: "Sicherheit",
    alleSicherheiten: "Alle Sicherheiten",
    filterBezug: "Bezug",
    alleBezuege: "Alle Bezüge",
    nurUnklar: "nur unklare",
    status: { vorgeschlagen: "Vorschlag", bestaetigt: "bestätigt", abgelehnt: "abgelehnt", hinfaellig: "hinfällig" } as Record<string, string>,
    statusFilter: { vorgeschlagen: "offen", bestaetigt: "bestätigt", abgelehnt: "abgelehnt" } as Record<string, string>,
    quelle: { ki: "KI", mensch: "Mensch", ortsteilfeld: "Ortsteil-Feld" } as Record<string, string>,
    // E21h (05 §16.8): das handschriftliche Ortsteil-Feld der Umfrage
    ortsteilfeld: {
      chip: "Ortsteil-Feld",
      chipTitel: "Antwort im Feld „Ortsteil“ der Umfrage — zählt als Wohnort des Kindes (nur der Stadtteil).",
      modell: "Modellwahl",
      ausserhalb: "außerhalb der Stadt",
      ausserhalbKnopf: "Außerhalb der Stadt",
      ausserhalbTitel: "Das Kind wohnt nicht in der Stadt (Nachbarort) — zählt als „außerhalb der Stadt“, ohne Stadtteil.",
      alsAusserhalb: "Als „außerhalb der Stadt“ bestätigt",
      nurFeld: "nur Ortsteil-Feld",
      schreibweise: (geschrieben: string, ziel: string) => `${geschrieben} → ${ziel}`,
      zielName: (name: string, stadtteil: string | null) => (stadtteil && stadtteil !== name ? `${name} (${stadtteil})` : name),
    },
    unklar: "unklar — bitte wählen",
    keinZiel: "kein Ort gewählt",
    alternativen: "Mögliche Orte",
    annehmen: "Annehmen",
    ablehnen: "Ablehnen",
    anderer: "Anderen wählen",
    nurWohnort: "Nur Wohnort (Stadtteil)",
    nurWohnortTitel: "Die Stelle meint, wo das Kind wohnt — gezählt wird nur der Stadtteil, nie die Straße.",
    waehlenUebernehmen: "Diesen nehmen",
    bezugBeimErsetzen: "Gemeint als",
    bezugOrt: "Ort (Ziel, Treffpunkt)",
    bezugWohnort: "Wohnort",
    angenommen: "Angenommen",
    abgelehnt: "Abgelehnt",
    ersetzt: "Ersetzt",
    alsWohnort: "Als Wohnort bestätigt",
    veraltet: "Inzwischen geändert — neu geladen.",
    wahlFehlt: "Bitte erst einen der möglichen Orte wählen.",
    stelleBelegt: "Diese Textstelle hat schon einen Ort.",
    geprueft: (wer: string, am: string) => `geprüft von ${wer} · ${am}`,
    tasten: "a annehmen · x ablehnen · w anderen wählen · h nur Wohnort · o außerhalb der Stadt · j/k weiter/zurück",
    sammel: (n: number) => `Alle sicheren übernehmen (${nf(n)})`,
    sammelTitel: "Alle sicheren Vorschläge übernehmen?",
    sammelText: (n: number) =>
      `${nf(n)} Vorschläge mit hoher Sicherheit und eindeutigem Ort werden bestätigt — ohne Wohnort-Bezug oder als exakt erkannter Ortsteil im Ortsteil-Feld. Andere Wohnorte, Straßen und unklare Vorschläge bleiben offen. Die Sammelannahme wird protokolliert.`,
    sammelErgebnis: (a: number, u: number) => `${nf(a)} angenommen${u ? ` · ${nf(u)} übersprungen` : ""}`,
    bogen: (c: string) => `Bogen ${c}`,
    region: (c: string) => `Beleg ${c}`,
    imPruefen: "Im Prüfen öffnen",
    // Abschnitt im Prüf-Beleg
    sektion: "Orte",
    sektionLeer: "Keine Ortsangabe in dieser Antwort.",
    // E21i (05 §17.3): die Antwort nennt die eigene Schule des Kindes — die kommt aus der Charge.
    sektionSchulfrage: "Frage nach der eigenen Schule — wird nicht verortet. Die Schule kommt aus der Charge (Ortsverzeichnis › Schulen).",
    hinzufuegen: "Ort hinzufügen",
    hinzufuegenHinweis: "Die Stelle im gespeicherten Text markieren, dann den Ort wählen.",
    feldWahl: "Text",
    stelleMarkieren: "Erst eine Stelle im Text markieren (höchstens 120 Zeichen).",
    stelle: (t: string) => `Stelle: „${t}“`,
    hinzugefuegt: "Ort hinzugefügt",
    gespeicherterText: "Gespeicherter Text — Änderungen im Entwurf zählen hier erst nach „Übernehmen“.",
    ortWaehlen: "Ort oder Stadtteil wählen",
    ortSuchen: "Ort oder Stadtteil suchen …",
    keinTreffer: "Kein Ort gefunden.",
    stadtteil: "Stadtteil",
  },
  admin: {
    titel: "Verwaltung",
    unterzeile: "Nutzer, Zugriffe, Verfahren, Datenquellen, Protokoll und Systemzustand.",
    reiter: { nutzer: "Nutzer", zugriff: "Zugriff", verfahren: "Verfahren", datenquellen: "Datenquellen", protokoll: "Protokoll", system: "System" },
    name: "Name",
    email: "E-Mail",
    rolle: "Rolle",
    aktiv: "Aktiv",
    letzteAnmeldung: "letzte Anmeldung",
    verfahren: "Verfahren",
    nie: "nie",
    letzterAdmin: "Der letzte aktive Admin kann nicht herabgestuft oder deaktiviert werden.",
    gespeichert: "Gespeichert",
    entsperrenTitel: "Anmeldesperre aufheben",
    entsperren: "Sperre aufheben",
    entsperrt: (n: number) => `${n} Sperreinträge entfernt`,
    zugriffVerfahren: "Verfahren",
    erteiltAm: "erteilt am",
    entziehen: "Entziehen",
    entzogen: "Zugriff entzogen",
    gewaehren: "Zugriff geben",
    gewaehrt: "Zugriff gegeben",
    nutzerWaehlen: "Nutzer wählen",
    keinZugriff: "Noch niemand hat Zugriff auf dieses Verfahren.",
    neuesVerfahren: "Neues Verfahren",
    verfahrenBearbeiten: "Verfahren bearbeiten",
    code: "Code",
    codeHinweis: "2–5 Großbuchstaben und zwei Ziffern, z. B. MU26",
    titelFeld: "Titel",
    kommune: "Kommune",
    art: "Art",
    synthetisch: "synthetisch",
    echt: "echt",
    echtHinweis: "Echte Bögen bleiben gesperrt, bis Victor AVV, DSFA und Betreiber bestätigt.",
    avv: "AVV-Referenz",
    loeschenBis: "Löschen bis",
    freigabe: "Freigabe",
    schwaerzen: "Namen vor dem Sehmodell schwärzen",
    schwaerzenHinweis: "Aus: Seiten gehen ohne Schwärzung an das lokale Sehmodell (E20). An: Seiten warten, bis sie geschwärzt oder freigegeben sind.",
    notiz: "Notiz",
    codeVergeben: "Der Code ist schon vergeben.",
    verfahrenAngelegt: (c: string) => `Verfahren ${c} angelegt`,
    protokollArt: "Art",
    alleArten: "Alle Arten",
    alleVerfahren: "Alle Verfahren",
    von: "von",
    bis: "bis",
    zeit: "Zeit",
    nutzer: "Nutzer",
    ziel: "Ziel",
    meta: "Meta",
    keineEintraege: "Keine Einträge für diese Filter.",
    db: "Datenbank",
    gex: "GEX44",
    platte: "Freier Speicher",
    platteKnapp: "knapp",
    echteDaten: "Echte Daten freigegeben",
    nurServer: "wird nur auf dem Server gesetzt",
    fassung: "Fassung",
    ja: "ja",
    nein: "nein",
    gexZustand: { bereit: "bereit", laedt: "lädt", aus: "aus", unerreichbar: "unerreichbar" } as Record<string, string>,
    // E29 (05 §18): Datenquellen — externe PostGIS-Datenbanken für „Orte → Laden → Aus Datenbank laden“
    dq: {
      text: "Datenbanken, aus denen das Ortsverzeichnis Ebenen liest (PostGIS, nur lesend). Alle mit dem Recht „Ortsverzeichnis“ (Admin, Prüfer) dürfen aktive Datenquellen nutzen.",
      neu: "Neue Datenquelle",
      bearbeiten: "Datenquelle bearbeiten",
      leer: "Noch keine Datenquelle.",
      name: "Name",
      namePlatzhalter: "z. B. psql.lilasp.de (Projekte)",
      host: "Server",
      hostHinweis: "Nur der Name oder die Adresse des Servers, ohne „postgres://“.",
      port: "Port",
      nutzer: "Nutzer",
      passwort: "Passwort",
      passwortHinweisNeu: "Wird verschlüsselt gespeichert und nie wieder angezeigt.",
      passwortHinweisAlt: "Leer lassen = unverändert. Ein neues ersetzt das gespeicherte; angezeigt wird es nie.",
      passwortUnveraendert: "unverändert",
      passwortZielNeu: "Neuer Server, Port oder Nutzer: bitte das Passwort neu eingeben — das gespeicherte gilt nur für das bisherige Ziel.",
      datenbanken: "Erlaubte Datenbanken",
      datenbankenHinweis:
        "Namen oder Muster, durch Komma getrennt; * steht für beliebige Zeichen (z. B. lilasp_*). Gelesen wird nur aus diesen; die Liste der Datenbanken holt die App aus dem ersten Namen ohne * oder sonst aus „postgres“ (nur lesend).",
      aktiv: "aktiv (nutzbar)",
      inaktiv: "abgeschaltet",
      voreinstellung: "Server-Voreinstellung",
      voreinstellungHinweis: "Kommt aus der Server-Umgebung (ORTE_PG_*) und wird nur dort geändert.",
      pwGesetzt: "Passwort gespeichert",
      pwFehlt: "ohne Passwort",
      pwNeu: "Passwort neu eingeben",
      angelegt: (wer: string | null, wann: string) => `angelegt ${wann}${wer ? ` von ${wer}` : ""}`,
      geaendert: (wer: string | null, wann: string) => `geändert ${wann}${wer ? ` von ${wer}` : ""}`,
      testen: "Verbindung testen",
      testLaeuft: "Verbindung wird getestet …",
      testOk: (server: string | null) => `Verbindung steht${server ? ` (PostgreSQL ${server})` : ""}.`,
      testKeine: "Verbunden, aber keine erlaubte Datenbank gefunden.",
      testWeitere: (n: number) => `und ${nf(n)} weitere`,
      postgis: (v: string) => `PostGIS ${v}`,
      ohnePostgis: "ohne PostGIS",
      nichtErreichbar: "nicht erreichbar",
      angelegtToast: "Datenquelle angelegt",
      musterFalsch: "Bitte nur Buchstaben, Ziffern, _, - und * (durch Komma getrennt).",
    },
  },
} as const;

/** Der Pausensatz zu `lauf.pausengrund` (01 §2.2.6) — Lauf-Bildschirm und Aufträge-Panel. */
export function pausenSatz(grund: PausenGrund | null, uhr: string): string {
  if (grund === "nicht_erreichbar") return TX.lauf.pauseNetz(uhr);
  if (grund === "modell_anders") return TX.lauf.pauseModell(uhr);
  return TX.lauf.pause(uhr);
}

/** Die Protokoll-Arten der Verwaltung (SPEC 03 §9.13). */
export const PROTOKOLL_ARTEN = [
  "ausschnitt",
  "seite",
  "original",
  "export",
  "export_download",
  "rolle",
  "zugriff",
  "maske_freigabe",
  "masken_erkennen",
  "kaestchen_messen",
  "schwaerzung_umgeschaltet",
  "fragen_gespeichert",
  "fragen_uebernommen",
  "fragen_zurueckgesetzt",
  "fragen_ki_angefordert",
  "tombstone",
  "assistent",
  "loeschung",
  "orte_datei",
  "orte_import",
  "orte_uebernehmen",
  "datenquelle",
  "verortung_sammel",
] as const;
